import React, { useState, useEffect, useRef } from 'react';
import QuoterHeader from './QuoterHeader';
import OptimizedQuoteForm from './OptimizedQuoteForm';
import HealthAnalyzerModal from './HealthAnalyzerModal';
import QuoteResults from './QuoteResults';
import LoadingState from './LoadingState';
import CarriersModal from './CarriersModal';
import ComparisonModal from '../annuities_quoter/ComparisonModal';
// Import QuoteCard components for details modal
import AnnuityQuoteCard from '../annuities_quoter/QuoteCard';
import LifeQuoteCard from '../alq/QuoteCard';
import Modal from '../shared/Modal';
import ShareQuotesModal from 'alq/ShareQuotesModal';
// Import required contexts for QuoteCard components
import { ThemeContext } from '../shared/ThemeContext';
import { ProductContext } from '../alq/Context';
// Import FirelightSimplifiedEticketModal
import FirelightSimplifiedEticketModal from '../alq/FirelightSimplifiedEticketModal';
// Import QuoteActionModals for both annuity and life
import AnnuityQuoteActionModal from '../annuities_quoter/QuoteActionModal';
import LifeQuoteActionModal from '../alq/QuoteActionModal';
// Import follow-up modals for custom actions
import OpenUrlModal from '../alq/OpenUrlModal';
import PostToUrlModal from '../alq/PostToUrlModal';
import PsgEappModal from '../alq/PsgEappModal';
import LifedxEticketModal from '../alq/LifedxEticketModal';
import RequestFormsModal from '../alq/RequestFormsModal';
import ApplicintModal from '../alq/ApplicintModal';
import NmbTermEticketModal from '../alq/nmb_term_eticket/Index';
import NmbWholeLifeEticketModal from '../alq/nmb_wl_eticket/Index';
import FirelightEticketModal from '../alq/FirelightEticketModal';
import FirelightEappModal from '../alq/FirelightEappModal';
import Loader from '../shared/Loader';
import {
  ANICO_EMBED_TOKEN,
  ANICO_TEST_EMBED_TOKEN,
  PACLIFE_EMBED_TOKEN,
  PACLIFE_TEST_EMBED_TOKEN,
  PROTECTIVE_EMBED_TOKEN,
  PROTECTIVE_TEST_EMBED_TOKEN,
  SBLI_EMBED_TOKEN,
  SBLI_TEST_EMBED_TOKEN
} from '../../config/App';
import AnnuitiesApi from '../../utils/AnnuitiesApi';
import AlqApi from '../../utils/AlqApi';
import HealthAnalyzerApi from '../../utils/HealthAnalyzerApi.js';
import { AMBEST_SORTING } from '../../constants/App';
import { QUOTER_TABS } from '../../constants/QuoterTabs';
import PdfActions from '../shared/PdfActions';
import QuoteComparisonModal from './QuoteComparisonModal';
import { ProductContext as AnnuityProductContext } from '../annuities_quoter/Context';

// Import custom hooks for state management
import { useModalManager, useQuoteForm, useCarrierSelection, useHealthAnalyzer, useQuoteData } from './hooks';
// Import shared quote utility helpers
import { hasQuoteValidActions } from './utils/quoteUtils';

const QUOTER_CONFIG = window.HEXURE_UQ_CONFIG || {};
const firelightIXNModalData = window.ixn_data?.IXNModel ?? window.ixnDataModel?.IXNModel ?? {};

const UniversalQuoterComponent = ({
  activeTab: propActiveTab,
  onTabChange,
  loading,
  error,
  aaqQuoter,
  alqQuoter,
  ltcQuoter,
  aaqCriteriaOptions,
  aaqLimitProductTypes,
  uqData,
  isUniversalQuoterLoaded,
  // Carrier data
  aaqCarriers,
  aaqProducts,
  aaqProductsFeatures,
  solveType,
  alqCarriers,
  alqProductFeatures,
  alqGuides,
  // Life product options
  lifeProductOptions,
  // Quoter configuration
  quoterConfig,
  onQuoterConfigChange,
  disableQuoter,
  savedQuoteId,
  // Tab-specific form data for unsaved quotes
  annuityTabFormData,
  lifeTabFormData,
  // Form data change handler
  onFormDataChange
}) => {
  // ============================================================================
  // REFACTORED STATE MANAGEMENT - Using Custom Hooks
  // ============================================================================

  // Basic component state (not in hooks yet)
  const [activeTab, setActiveTab] = useState(propActiveTab || 'new-life');
  const [currentView, setCurrentView] = useState('form'); // 'form', 'loading', or 'results'

  // Ref to track initial mount to avoid calling onFormDataChange on first render
  const isInitialMount = useRef(true);

  // Initialize custom hooks for state management
  const modalState = useModalManager();
  const formState = useQuoteForm(null, onFormDataChange);
  const carrierState = useCarrierSelection(activeTab, aaqCarriers, alqCarriers, formState.formData, formState.updateField);
  const healthAnalyzerState = useHealthAnalyzer();
  const quoteDataState = useQuoteData();

  // Destructure commonly used values for backward compatibility
  const { formData, updateField, setFormData } = formState;
  const {
    openModal,
    setOpenModal,
    isQuoteSetupOverlayOpen,
    setIsQuoteSetupOverlayOpen,
    overlayFormData,
    setOverlayFormData,
    isCompareModalOpen,
    setIsCompareModalOpen,
    compareModalData,
    setCompareModalData,
    isDetailsModalOpen,
    setIsDetailsModalOpen,
    detailsModalData,
    setDetailsModalData,
    isFirelightModalOpen,
    setIsFirelightModalOpen,
    firelightModalData,
    setFirelightModalData,
    isQuoteActionModalOpen,
    setIsQuoteActionModalOpen,
    quoteActionModalData,
    setQuoteActionModalData,
    hideQuoteCardDetails,
    setHideQuoteCardDetails,
    followUpModalType,
    setFollowUpModalType,
    followUpModalData,
    setFollowUpModalData,
    psgEapp,
    setPsgEapp,
    anicoEticketGuid,
    setAnicoEticketGuid,
    paclifeEticketGuid,
    setPaclifeEticketGuid,
    protectiveEticketGuid,
    setProtectiveEticketGuid,
    sbliEticketGuid,
    setSbliEticketGuid,
    nmbEticketGuid,
    setNmbEticketGuid,
    firelightEticket,
    setFirelightEticket,
    firelightEapp,
    setFirelightEapp,
    showPdfModal,
    setShowPdfModal,
    isShareModelOpen,
    setIsShareModelOpen,
    shareModelData,
    setShareModelData
  } = modalState;

  const { hasUserEditedLifeCarriers, setHasUserEditedLifeCarriers, hasAutoSelectedAnnuity, setHasAutoSelectedAnnuity, userClearedAnnuity, setUserClearedAnnuity, resetLifeCarriersToAll } =
    carrierState;

  const { underwritingClasses, setUnderwritingClasses, healthAnalyzerResults, setHealthAnalyzerResults, clearHealthAnalyzerResults } = healthAnalyzerState;

  const {
    quotesByProductType,
    setQuotesByProductType,
    originalQuotesByProductType,
    setOriginalQuotesByProductType,
    loadingStates,
    setLoadingStates,
    quotesError,
    setQuotesError,
    hasInitiatedQuoteRequest,
    setHasInitiatedQuoteRequest,
    appliedFilters,
    setAppliedFilters
  } = quoteDataState;

  // Additional state not yet in hooks
  const [compareSelections, setCompareSelections] = useState({});
  const [carrierFilter, setCarrierFilter] = useState({});
  const [isCarriersModalFilterMode, setIsCarriersModalFilterMode] = useState(false);
  const [healthAnalyzerCarriers, setHealthAnalyzerCarriers] = useState([]);
  const [confirmationState, setConfirmationState] = useState(null);

  // Refs
  const manualAnnuityEditRef = useRef(false);
  const prevActiveTabRef = useRef(activeTab);

  // ============================================================================
  // END OF STATE MANAGEMENT
  // ============================================================================

  // set default product type and solveFor if not already set
  useEffect(() => {
    if (activeTab === 'new-annuity') {
      // Only set default if productTypes is empty (new unsaved quote, not loaded from saved data)
      if (!formData.productTypes || formData.productTypes.length === 0) {
        const updatedProductTypes = aaqCriteriaOptions?.product_types?.slice(0, 1) || ['FA'];
        setFormData({ ...formData, productTypes: updatedProductTypes, solveFor: 'Accumulation' });

        // If overlay is open, also update overlay form data
        if (isQuoteSetupOverlayOpen && overlayFormData) {
          setOverlayFormData({ ...overlayFormData, productTypes: updatedProductTypes, solveFor: 'Accumulation' });
        }
      }
    }
    if (activeTab === 'new-life') {
      // Set default life product tab and solveFor if not already set (don't override saved values)
      const needsDefaults = !formData.activeProductTab || !formData.solveFor;
      if (needsDefaults) {
        const lifeProductTab = formData.activeProductTab || 'TERM';
        const lifeSolveFor = formData.solveFor || 'Premium';
        setFormData({ ...formData, activeProductTab: lifeProductTab, solveFor: lifeSolveFor });

        // If overlay is open, also update overlay form data
        if (isQuoteSetupOverlayOpen && overlayFormData) {
          setOverlayFormData({ ...overlayFormData, activeProductTab: lifeProductTab, solveFor: lifeSolveFor });
        }
      }
    }
  }, [aaqCriteriaOptions, activeTab, lifeProductOptions, isQuoteSetupOverlayOpen]);

  // Load tab-specific form data when switching tabs (for unsaved quotes)
  useEffect(() => {
    // Only restore from tab-specific state if this is an unsaved quote
    if (!savedQuoteId) {
      if (activeTab === 'new-annuity' && annuityTabFormData) {
        setFormData(prev => ({ ...prev, ...annuityTabFormData }));
      } else if (activeTab === 'new-life' && lifeTabFormData) {
        setFormData(prev => ({ ...prev, ...lifeTabFormData }));
      }
    }
  }, [activeTab, savedQuoteId, annuityTabFormData, lifeTabFormData]);

  // Get available carriers from quote results (for filtering)
  // Note: Similar functionality is available in carrierState.getAvailableCarriersFromQuotes
  const getAvailableCarriersFromQuotes = () => {
    return carrierState.getAvailableCarriersFromQuotes(quotesByProductType, originalQuotesByProductType);
  };

  // Get available carriers based on active tab
  // Note: This uses carrierState.getAvailableCarriers under the hood
  const getAvailableCarriers = () => {
    return carrierState.getAvailableCarriers();
  };

  // Get current form carriers - ensure they match the current tab's available carriers
  // Note: Similar functionality is available in carrierState.getCurrentFormCarriers
  const getCurrentFormCarriers = () => {
    const dataToUse = isQuoteSetupOverlayOpen && overlayFormData ? overlayFormData : formData;
    const availableCarriers = getAvailableCarriers();
    // Compare IDs as strings to avoid type mismatches (number vs string)
    const availableCarrierIds = new Set(availableCarriers.map(c => String(c.id)));

    // Filter selected carriers to only include ones that are valid for current tab
    return (dataToUse.selectedCarriers || []).filter(carrierId => availableCarrierIds.has(String(carrierId)));
  };

  // Compare two carrier arrays for equality
  // Note: Similar functionality is available in carrierState.areCarriersEqual
  function areCarriersEqual(arr1, arr2) {
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
      return false;
    }

    if (arr1.length !== arr2.length) {
      return false;
    }
    const set1 = new Set(arr1.map(c => `${c.value}|${c.name}`));
    const set2 = new Set(arr2.map(c => `${c.value}|${c.name}`));

    if (set1.size !== set2.size) {
      return false;
    }

    for (const item of set1) {
      if (!set2.has(item)) return false;
    }

    return true;
  }

  // Note: healthAnalyzerCarriers, confirmationState, carrierFilter, and isCarriersModalFilterMode
  // are now declared in the state management section above

  // Ref to track fetch request status and prevent duplicate calls
  const haCarriersFetchedRef = useRef(false);
  const haCarriersFetchingRef = useRef(false);

  useEffect(() => {
    // Prevent multiple simultaneous requests
    if (haCarriersFetchingRef.current || haCarriersFetchedRef.current) {
      return;
    }

    const abortController = new AbortController();

    async function fetchHaCarriers() {
      // Mark as fetching to prevent duplicate requests
      haCarriersFetchingRef.current = true;

      try {
        // HealthAnalyzerApi.getCarriers() returns the parsed response data (array of carriers)
        // Pass abort signal to cancel request if component unmounts
        const res = await HealthAnalyzerApi.getCarriers({ signal: abortController.signal });

        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }

        const carriers = Array.isArray(res) ? res : res.data || [];
        const mappedCarriers = carriers.map(carrier => ({ value: carrier.id, label: carrier.name }));
        setHealthAnalyzerCarriers(mappedCarriers);
        haCarriersFetchedRef.current = true;
      } catch (err) {
        // Don't log error if request was aborted
        if (!abortController.signal.aborted) {
          // Check if it's a rate limit error
          if (err.response && err.response.status === 429) {
            console.warn('Health Analyzer API rate limit reached. Carriers will not be available for this session.');
          } else {
            console.error('Error fetching Health Analyzer carriers:', err);
          }
          setHealthAnalyzerCarriers([]);
        }
      } finally {
        haCarriersFetchingRef.current = false;
      }
    }

    fetchHaCarriers();

    // Cleanup: abort request if component unmounts
    return () => {
      abortController.abort();
    };
  }, []);

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      // Clear embed config quote actions on unmount
      if (typeof window !== 'undefined') {
        delete window.UQ_EMBED_CONFIG_QUOTE_ACTIONS;
      }
    };
  }, []);

  // Sync form data with parent component (skip initial render to avoid updating during mount)
  useEffect(() => {
    // Skip the initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only notify parent of changes after initial mount
    if (onFormDataChange) {
      // Defer to next tick to avoid updating parent during render
      const timeoutId = setTimeout(() => {
        onFormDataChange(formData);
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [formData]);

  // Sync with prop changes
  useEffect(() => {
    if (propActiveTab && propActiveTab !== activeTab) {
      setActiveTab(propActiveTab);

      // Clear selected carriers when switching tabs since different tabs have different carriers
      setFormData(prev => ({
        ...prev,
        selectedCarriers: []
      })); // Clear quotes data when switching between different quote types via props
      setQuotesByProductType({});
      setOriginalQuotesByProductType({});

      // Clear embed config quote actions
      if (typeof window !== 'undefined') {
        delete window.UQ_EMBED_CONFIG_QUOTE_ACTIONS;
      }

      // Clear carrier filter state
      setCarrierFilter({});

      // Clear compare selections
      setCompareSelections({});
      // Reset annuity auto-select flag when parent forces a tab change
      setHasAutoSelectedAnnuity(false);
      manualAnnuityEditRef.current = false;
      setUserClearedAnnuity(false);

      // When switching tabs via prop change, clear Health Analyzer state so
      // each tab starts fresh and life carriers can auto-select again.
      setUnderwritingClasses([]);
      setHealthAnalyzerResults({
        carrierValues: [],
        mappedCarrierIds: [],
        productIds: [],
        healthClasses: []
      });
      setHasUserEditedLifeCarriers(false);
    }
  }, [propActiveTab, activeTab]);
  useEffect(() => {
    const prevActiveTab = prevActiveTabRef.current;
    prevActiveTabRef.current = activeTab;

    // When (re)entering the life tab, clear any lingering HA state and
    // ensure the main form has all life carriers selected by default.
    if (activeTab !== 'new-life') {
      return;
    }

    // Do not clear HA on initial mount while already on new-life;
    // only clear when we return to new-life from a different tab.
    if (prevActiveTab === 'new-life') {
      return;
    }

    const hasHaCarrierState =
      (Array.isArray(underwritingClasses) && underwritingClasses.length > 0) ||
      (healthAnalyzerResults &&
        ((Array.isArray(healthAnalyzerResults.mappedCarrierIds) && healthAnalyzerResults.mappedCarrierIds.length > 0) ||
          (Array.isArray(healthAnalyzerResults.carrierValues) && healthAnalyzerResults.carrierValues.length > 0)));

    if (hasHaCarrierState) {
      setUnderwritingClasses([]);
      setHealthAnalyzerResults({
        carrierValues: [],
        mappedCarrierIds: [],
        productIds: [],
        healthClasses: []
      });
    }

    setHasUserEditedLifeCarriers(false);

    if ((!Array.isArray(formData.selectedCarriers) || formData.selectedCarriers.length === 0) && Array.isArray(alqCarriers) && alqCarriers.length > 0) {
      const allCarrierIds = alqCarriers.map(c => c.id);
      setFormData(prev => ({
        ...prev,
        selectedCarriers: allCarrierIds
      }));

      // If overlay is open, also update overlay form data
      if (isQuoteSetupOverlayOpen && overlayFormData) {
        setOverlayFormData(prev => ({
          ...prev,
          selectedCarriers: allCarrierIds
        }));
      }
    }
  }, [activeTab, underwritingClasses, healthAnalyzerResults, alqCarriers, formData.selectedCarriers, isQuoteSetupOverlayOpen, overlayFormData]);

  useEffect(() => {
    // Auto-select all carriers when entering a tab with carriers loaded
    // and no selection has been made yet. For life, only do this on first
    // entry before HA results and before the user has edited carriers.
    const availableCarriers = getAvailableCarriers();
    if (!availableCarriers || availableCarriers.length === 0) return;

    if (activeTab === 'new-life') {
      const haHasResults =
        (Array.isArray(underwritingClasses) && underwritingClasses.length > 0) ||
        (healthAnalyzerResults && Array.isArray(healthAnalyzerResults.mappedCarrierIds) && healthAnalyzerResults.mappedCarrierIds.length > 0);

      // Do not auto-select if HA has results. If the user has manually edited
      // carriers, we also avoid auto-selecting, but HA itself remains
      // authoritative when it runs.
      if (haHasResults || hasUserEditedLifeCarriers) return;
    }

    // For annuity, auto-select once when we first enter the tab and carriers
    // are available and no carrier selection exists yet. Do not overwrite user
    // actions afterwards. Use a flag to ensure this happens only once.
    if (activeTab === 'new-annuity') {
      if (!hasAutoSelectedAnnuity && (!Array.isArray(formData.selectedCarriers) || formData.selectedCarriers.length === 0)) {
        const allCarrierIds = availableCarriers.map(carrier => carrier.id);
        // If the user recently cleared annuity carriers, do not auto-select here
        if (userClearedAnnuity) {
          return;
        }

        setFormData(prev => ({
          ...prev,
          selectedCarriers: allCarrierIds
        }));

        // If overlay is open, also update overlay form data
        if (isQuoteSetupOverlayOpen && overlayFormData) {
          setOverlayFormData(prev => ({
            ...prev,
            selectedCarriers: allCarrierIds
          }));
        }

        // Only mark auto-selected if there wasn't a very recent manual edit
        if (!manualAnnuityEditRef.current) {
          setHasAutoSelectedAnnuity(true);
        }
      }
    } else {
      // For non-annuity tabs, keep previous behaviour
      if (!Array.isArray(formData.selectedCarriers) || formData.selectedCarriers.length === 0) {
        const allCarrierIds = availableCarriers.map(carrier => carrier.id);
        setFormData(prev => ({
          ...prev,
          selectedCarriers: allCarrierIds
        }));

        // If overlay is open, also update overlay form data
        if (isQuoteSetupOverlayOpen && overlayFormData) {
          setOverlayFormData(prev => ({
            ...prev,
            selectedCarriers: allCarrierIds
          }));
        }
      }
    }
  }, [activeTab, aaqCarriers, alqCarriers, underwritingClasses, healthAnalyzerResults, formData.selectedCarriers.length, hasUserEditedLifeCarriers, isQuoteSetupOverlayOpen]);

  // Update form data when quoter configuration changes
  useEffect(() => {
    if (quoterConfig) {
      setFormData(prev => {
        const updated = { ...prev };

        // Update agent information
        if (quoterConfig.agent) {
          updated.firstName = quoterConfig.agent?.first_name || prev.firstName;
          updated.lastName = quoterConfig.agent?.last_name || prev.lastName;
          updated.agentEmail = quoterConfig.agent?.email || prev.agentEmail;
          updated.agentPhone = quoterConfig.agent?.phone || prev.agentPhone;
          updated.agentLicenseNumber = quoterConfig.agent?.insurance_license_number || prev.agentLicenseNumber;
        }
        // Update proposed insured information if available - handle nested structure
        if (quoterConfig.proposed_insured) {
          // Sync based on active tab - use separate fields for annuity and life
          if (activeTab === 'new-annuity' && quoterConfig.proposed_insured.annuity) {
            updated.proposedInsuredAnnuityFirstName = quoterConfig.proposed_insured.annuity?.first_name || prev.proposedInsuredAnnuityFirstName;
            updated.proposedInsuredAnnuityLastName = quoterConfig.proposed_insured.annuity?.last_name || prev.proposedInsuredAnnuityLastName;
            updated.dateOfBirthAnnuity = quoterConfig.proposed_insured.annuity?.date_of_birth || prev.dateOfBirthAnnuity;
            updated.genderAnnuity = quoterConfig.proposed_insured.annuity?.gender || prev.genderAnnuity;
            updated.jurisdictionAnnuity = quoterConfig.proposed_insured.annuity?.state || prev.jurisdictionAnnuity;
            // Update generic jurisdiction field for annuity tab
            if (quoterConfig.proposed_insured.annuity?.state) {
              updated.jurisdiction = quoterConfig.proposed_insured.annuity.state;
            }
          } else if (activeTab === 'new-life' && quoterConfig.proposed_insured.life) {
            updated.proposedInsuredLifeFirstName = quoterConfig.proposed_insured.life?.first_name || prev.proposedInsuredLifeFirstName;
            updated.proposedInsuredLifeLastName = quoterConfig.proposed_insured.life?.last_name || prev.proposedInsuredLifeLastName;
            updated.dateOfBirthLife = quoterConfig.proposed_insured.life?.date_of_birth || prev.dateOfBirthLife;
            updated.genderLife = quoterConfig.proposed_insured.life?.gender || prev.genderLife;
            updated.jurisdictionLife = quoterConfig.proposed_insured.life?.state || prev.jurisdictionLife;
            // Update generic jurisdiction field for life tab
            if (quoterConfig.proposed_insured.life?.state) {
              updated.jurisdiction = quoterConfig.proposed_insured.life.state;
            }
          }
        }

        // Update quote data
        if (quoterConfig.quote_data) {
          const qd = quoterConfig.quote_data;
          if (qd.premium) {
            updated.premiumLife = qd.premium;
            updated.premiumAnnuity = qd.premium;
          }

          // Update common fields
          if (qd.state) updated.jurisdiction = qd.state;
          if (typeof qd.tobacco !== 'undefined') updated.tobacco = qd.tobacco;

          // Update face amounts for life insurance
          if (qd.face_amounts && qd.face_amounts.length > 0) {
            updated.faceAmounts = qd.face_amounts;
            updated.faceAmount = qd.face_amounts[0];
          }

          // Update carrier selection
          if (qd.carrier_ids && Array.isArray(qd.carrier_ids)) {
            updated.selectedCarriers = qd.carrier_ids;
          }

          // Update product types based on active tab
          if (activeTab === 'new-life' && qd.product_types_life && Array.isArray(qd.product_types_life)) {
            updated.productTypes = qd.product_types_life;
            updated.selectedProductTypes = qd.product_types_life;
          } else if (activeTab === 'new-annuity' && qd.product_types && Array.isArray(qd.product_types) && qd.product_types.length > 0) {
            // For annuity, use the already-converted product_types from quoterConfig
            updated.productTypes = qd.product_types;
            updated.selectedProductTypes = qd.product_types;
          } else if (qd.product_types && Array.isArray(qd.product_types)) {
            // Fallback to generic field
            updated.productTypes = qd.product_types;
            updated.selectedProductTypes = qd.product_types;
          }

          // Update riders
          if (qd.riders && Array.isArray(qd.riders)) {
            // Filter out empty strings and map riders to form fields
            const validRiders = qd.riders.filter(r => r && r.trim() !== '');
            updated.adb_rider = validRiders.some(r => r.toLowerCase().includes('adb') || r.toLowerCase().includes('death'));
            updated.wop_rider = validRiders.some(r => r.toLowerCase().includes('wop') || r.toLowerCase().includes('waiver'));
          }

          // Update child rider units
          if (qd.child_rider_units) {
            updated.child_rider_units = qd.child_rider_units;
          }

          // Update premium mode for life insurance (no activeTab check needed - this is life-specific data)
          if (qd.premium_mode) {
            updated.premiumMode = qd.premium_mode;
          }

          // Update flat extra
          if (qd.flat_extra) {
            updated.flatExtra = qd.flat_extra;
          }

          // Update health categories
          if (qd.health_categories && Array.isArray(qd.health_categories)) {
            updated.healthClass = qd.health_categories;

            // If any of the health categories are table ratings (Table A-V), set showTableRatings to true
            const TABLE_RATINGS = Array.from({ length: 22 }, (_, i) => `Table ${String.fromCharCode(65 + i)}`);
            const hasTableRatings = qd.health_categories.some(hc => TABLE_RATINGS.includes(hc));
            if (hasTableRatings) {
              updated.showTableRatings = true;
            }
          }

          // RTB-242: Update tax treatment for annuity quotes
          if (qd.taxTreatment) {
            updated.taxTreatment = qd.taxTreatment;
          }

          if (quoterConfig.quote_data?.activeProductTab !== undefined) {
            updated.activeProductTab = quoterConfig.quote_data.activeProductTab;
          }

          // Load quote_value if available
          if (qd.quote_value !== undefined) {
            updated.quoteValue = qd.quote_value;
          }

          // Load solve_for based on active tab
          if (activeTab === 'new-annuity' && qd.solve_for_annuity) {
            updated.solveFor = qd.solve_for_annuity;
          } else if (activeTab === 'new-life' && qd.solve_for_life) {
            updated.solveFor = qd.solve_for_life;
          }
        }

        return updated;
      });
    }
  }, [quoterConfig, activeTab]);

  const handleTabChange = tabId => {
    const isLoading = Object.values(loadingStates).some(value => value === true);
    if (Object.keys(loadingStates).length > 0 && isLoading) return;

    // Only perform tab change actions if the selected tab is different
    if (tabId !== activeTab) {
      // Tab-specific form data is now saved in parent component (UniversalQuoter.js)
      // via handleTabChange callback before setActiveTab is called

      setActiveTab(tabId);
      setCurrentView('form');

      // For saved quotes or if no localStorage data, clear selected carriers when switching tabs
      // For unsaved quotes with localStorage data, carriers will be loaded from localStorage
      if (savedQuoteId) {
        setFormData(prev => ({
          ...prev,
          selectedCarriers: []
        }));
      }

      // Clear quotes data when switching between different quote types
      setQuotesByProductType({});
      setOriginalQuotesByProductType({});

      // Clear embed config quote actions
      if (typeof window !== 'undefined') {
        delete window.UQ_EMBED_CONFIG_QUOTE_ACTIONS;
      }

      // Clear carrier filter state
      setCarrierFilter({});

      // Clear compare selections
      setCompareSelections({});
      // Reset annuity auto-select state so entering annuity will re-run auto-select once
      setHasAutoSelectedAnnuity(false);
      //clear HA + life - carrier edit state on any tab switch
      setUnderwritingClasses([]);
      setHealthAnalyzerResults({
        carrierValues: [],
        mappedCarrierIds: [],
        productIds: [],
        healthClasses: []
      });
      setHasUserEditedLifeCarriers(false);

      // Call the parent's onTabChange to load the appropriate data
      if (onTabChange) {
        onTabChange(tabId);
      }
    }
  };
  // handleInputChange wrapper to maintain backward compatibility
  const handleInputChange = (field, value) => {
    // Clear error when user makes changes
    if (quotesError) {
      setQuotesError(null);
    }

    // Use the hook's updateField method
    updateField(field, value);

    // Sync tab-specific fields with generic fields for backwards compatibility
    // When tab-specific field changes, also update the generic field
    if (field === 'jurisdictionLife' || field === 'jurisdictionAnnuity') {
      // If we're updating the active tab's jurisdiction, sync to generic jurisdiction field
      const isActiveTabField = (activeTab === 'new-life' && field === 'jurisdictionLife') || (activeTab === 'new-annuity' && field === 'jurisdictionAnnuity');
      if (isActiveTabField) {
        updateField('jurisdiction', value);
      }
    } else if (field === 'dateOfBirthLife' || field === 'dateOfBirthAnnuity') {
      const isActiveTabField = (activeTab === 'new-life' && field === 'dateOfBirthLife') || (activeTab === 'new-annuity' && field === 'dateOfBirthAnnuity');
      if (isActiveTabField) {
        updateField('dateOfBirth', value);
      }
    } else if (field === 'genderLife' || field === 'genderAnnuity') {
      const isActiveTabField = (activeTab === 'new-life' && field === 'genderLife') || (activeTab === 'new-annuity' && field === 'genderAnnuity');
      if (isActiveTabField) {
        updateField('gender', value);
      }
    }
  };

  // Note: resetLifeCarriersToAll is now provided by the useCarrierSelection hook

  const handleModal = modalName => {
    if (modalName === 'carriers') {
      // If we're in the Quote Setup overlay, always open in selection mode (not filter mode)
      // If we're viewing results and NOT in overlay, open in filter mode
      if (isQuoteSetupOverlayOpen) {
        setIsCarriersModalFilterMode(false); // Selection mode when in overlay
      } else if (currentView === 'results') {
        setIsCarriersModalFilterMode(true); // Filter mode when viewing results
      } else {
        setIsCarriersModalFilterMode(false); // Selection mode for form view
      }

      // If we are in the main life form view and there are Health Analyzer results,
      // reset main form carriers back to all ALQ carriers so the dialog opens with
      // everything selected again after confirming discarding HA results.
      if (
        !isQuoteSetupOverlayOpen &&
        currentView === 'form' &&
        activeTab === 'new-life' &&
        ((Array.isArray(underwritingClasses) && underwritingClasses.length > 0) ||
          (healthAnalyzerResults && Array.isArray(healthAnalyzerResults.mappedCarrierIds) && healthAnalyzerResults.mappedCarrierIds.length > 0))
      ) {
        // User is entering the main life carriers dialog after having HA results.
        // Discard HA results and reset carriers back to all ALQ carriers so they
        // can manually adjust from the full list.
        clearHealthAnalyzerResults();
        resetLifeCarriersToAll();
        // After this point, user interactions with the life carriers modal
        // will be considered manual edits.
        setHasUserEditedLifeCarriers(true);
      }

      setOpenModal(modalName);
    } else if (modalName === 'health-analyzer') {
      // If Health Analyzer already produced results, show the same
      // confirmation dialog used for main carrier selection changes.
      const hasHaResults =
        (Array.isArray(underwritingClasses) && underwritingClasses.length > 0) ||
        (healthAnalyzerResults &&
          ((Array.isArray(healthAnalyzerResults.mappedCarrierIds) && healthAnalyzerResults.mappedCarrierIds.length > 0) ||
            (Array.isArray(healthAnalyzerResults.carrierValues) && healthAnalyzerResults.carrierValues.length > 0)));

      if (hasHaResults) {
        setConfirmationState({
          title: 'Please confirm',
          message: 'Changing carriers will discard current health analysis results. Continue?',
          onConfirm: () => {
            // Clear existing HA state so the next run starts fresh.
            clearHealthAnalyzerResults();
            setOpenModal(modalName);
          }
        });
        return;
      }

      setOpenModal(modalName);
    } else if (modalName === 'carriers-filter') {
      // Explicit filter mode (if needed)
      setIsCarriersModalFilterMode(true);
      setOpenModal('carriers');
    } else {
      setOpenModal(modalName);
    }
    // Add other modal types here if needed
  };

  const handleModalClose = () => {
    setOpenModal(null);
    setIsCarriersModalFilterMode(false);
  };

  const handleQuoteSetupToggle = () => {
    if (isQuoteSetupOverlayOpen) {
      closeQuoteSetupOverlay();
    } else {
      openQuoteSetupOverlay();
    }
  };

  // Handle comparison modal close
  const handleCompareModalClose = () => {
    setIsCompareModalOpen(false);
    setCompareModalData(null);
  };

  // Handle details modal close
  const handleDetailsModalClose = () => {
    setIsDetailsModalOpen(false);
    setDetailsModalData(null);
  };

  // Handle FirelightSimplifiedEticketModal
  const handleOpenFirelightModal = data => {
    setFirelightModalData(data);
    setIsFirelightModalOpen(true);
  };

  const handleFirelightModalClose = () => {
    setIsFirelightModalOpen(false);
    setFirelightModalData(null);
  };
  // Handle QuoteActionModal
  const handleQuoteActionModalClose = () => {
    setIsQuoteActionModalOpen(false);
    setQuoteActionModalData(null);
    // Reset hideQuoteCardDetails when closing the action modal
    setHideQuoteCardDetails(false);
  };

  // Handle life insurance quote actions - mapping action types to modal names
  const actionTypeToModal = {
    anico: 'anico',
    request_forms: 'request_forms',
    webhook_post: 'post_to_url',
    simple_url: 'open_url',
    default: 'open_url',
    typeform: 'open_url',
    formassembly: 'open_url',
    '123_contact_form': 'open_url',
    cognito_form: 'open_url',
    formsite: 'open_url',
    jotform: 'open_url',
    formstack: 'open_url',
    wufoo: 'open_url',
    google_form: 'open_url',
    applicant: 'applicint',
    nmb_term_eticket: 'nmb_term_eticket',
    nmb_whole_life_eticket: 'nmb_whole_life_eticket',
    paclife: 'paclife',
    protective: 'protective',
    firelight: 'firelight',
    simplified_eticket: 'simplified_eticket',
    psg_eapp: 'psg_eapp',
    download_quotes_pdf: 'share',
    sbli: 'sbli',
    send_quote_as_email: 'share',
    send_quote_as_text: 'share'
  };

  const handleLifeQuoteAction = action => {
    // Check if this action should be handled internally by the modal
    // PDF actions and simplified_eticket actions are handled internally by QuoteActionModal
    const isPdfAction = ['download_quotes_pdf', 'send_quote_as_email', 'send_quote_as_text'].includes(action.action_type);
    const isSimplifiedEticket = action.action_data?.activity === 'simplified_eticket';

    if (isPdfAction || isSimplifiedEticket) {
      // These actions are handled internally by the QuoteActionModal
      return;
    }

    // Map action type to modal name
    const modalType = actionTypeToModal[action.action_type] || 'open_url';

    // Handle different action types
    switch (modalType) {
      case 'open_url':
        setFollowUpModalType('open_url');
        setFollowUpModalData(action);
        break;
      case 'post_to_url':
        setFollowUpModalType('webhook_post');
        setFollowUpModalData(action);
        break;
      case 'psg_eapp':
        setFollowUpModalType('psg_eapp');
        setFollowUpModalData(action);
        break;
      case 'anico':
        setFollowUpModalType('anico');
        setFollowUpModalData(action);
        break;
      case 'protective':
        setFollowUpModalType('protective');
        setFollowUpModalData(action);
        break;
      case 'paclife':
        setFollowUpModalType('paclife');
        setFollowUpModalData(action);
        break;
      case 'sbli':
        setFollowUpModalType('sbli');
        setFollowUpModalData(action);
        break;
      case 'nmb_term_eticket':
        setFollowUpModalType('nmb_term_eticket');
        setFollowUpModalData(action);
        break;
      case 'nmb_whole_life_eticket':
        setFollowUpModalType('nmb_whole_life_eticket');
        setFollowUpModalData(action);
        break;
      case 'applicint':
        setFollowUpModalType('applicint');
        setFollowUpModalData(action);
        break;
      case 'firelight':
        setFollowUpModalType('firelight');
        setFollowUpModalData(action);
        break;
      case 'request_forms':
        setFollowUpModalType('request_forms');
        setFollowUpModalData(action);
        break;
      default:
        setFollowUpModalType('open_url');
        setFollowUpModalData(action);
    }
  };

  // Helper function to add CUSIPs to quotes (for FireLight integration)
  const addCusipsToQuotes = (products, quotes) => {
    return quotes.map(quote => {
      const quoted_product = products.find(product => quote.product_id === product.id);
      return { ...quote, product_cusip: quoted_product?.cusip };
    });
  };

  // Helper function to add embed config quote actions to ALQ quoter state
  const addEmbedConfigQuoteActions = quotes => {
    if (!QUOTER_CONFIG.quote_actions || !QUOTER_CONFIG.quote_actions.length) {
      return;
    }

    const embed_config_quote_actions = [];

    QUOTER_CONFIG.quote_actions.forEach(quote_action => {
      const enabled_quote_guids = [];

      quotes.forEach(quote => {
        if (quote_action?.should_show && quote_action.should_show(quote)) {
          enabled_quote_guids.push(quote.guid);
        }
      });

      embed_config_quote_actions.push({ ...quote_action, enabled_quote_guids });
    });

    // Update the alqQuoter with embed_config_quote_actions
    // Since we can't directly modify the prop, we'll store it in a way that can be accessed by the QuoteActionModal
    if (typeof window !== 'undefined') {
      window.UQ_EMBED_CONFIG_QUOTE_ACTIONS = embed_config_quote_actions;
    }
  };

  const handleCarriersChange = carriers => {
    // Normalize carriers to IDs - handle both objects and IDs
    const carrierIds = carriers.map(c => (typeof c === 'object' ? c.id : c));
    // Any change coming from the life carriers modal should be treated as a
    // user edit so auto-select-all does not re-run after they clear carriers.
    // Set user-edit flags before we update form state to avoid a race where
    // the auto-select useEffect fires based on the carrier array change
    // before these flags are applied.
    if (activeTab === 'new-life' && !isCarriersModalFilterMode) {
      setHasUserEditedLifeCarriers(true);
    }

    // For annuity, treat any user-driven change as a manual edit so the
    // auto-select-once behavior does not override a user's Clear All action.
    if (activeTab === 'new-annuity' && !isCarriersModalFilterMode) {
      manualAnnuityEditRef.current = true;
      setHasAutoSelectedAnnuity(true);
    }

    // Remember if the user explicitly cleared annuity carriers so the auto-select
    // effect won't immediately re-populate them.
    if (activeTab === 'new-annuity') {
      if (carrierIds.length === 0) setUserClearedAnnuity(true);
      else setUserClearedAnnuity(false);
    }

    if (isQuoteSetupOverlayOpen && overlayFormData) {
      // Update overlay data if we're in overlay mode
      setOverlayFormData(prev => ({
        ...prev,
        selectedCarriers: carrierIds
      }));
    } else {
      // Update main form data
      setFormData(prev => ({
        ...prev,
        selectedCarriers: carrierIds
      }));
    }
  };

  // Handle carrier filter changes
  const handleCarrierFilterChange = (carriersOrProductTypeData, productType = null) => {
    if (typeof carriersOrProductTypeData === 'object' && !Array.isArray(carriersOrProductTypeData) && productType === null) {
      //product-type-specific data (object with keys as product types)
      setCarrierFilter(carriersOrProductTypeData);
    } else if (Array.isArray(carriersOrProductTypeData)) {
      //legacy format (array of carriers)
      const carrierIds = carriersOrProductTypeData.map(c => (typeof c === 'object' ? c.id : c));

      if (productType) {
        // Update filter for specific product type
        setCarrierFilter(prev => ({
          ...prev,
          [productType]: carrierIds
        }));
      } else {
        // Legacy support - apply to all product types (when called from old interface)
        const allProductTypes = Object.keys(quotesByProductType);
        const newFilter = {};
        allProductTypes.forEach(type => {
          newFilter[type] = carrierIds;
        });
        setCarrierFilter(newFilter);
      }
    }
  };

  // Clear carrier filter
  const handleClearCarrierFilter = (productType = null) => {
    if (productType) {
      // Clear filter for specific product type
      setCarrierFilter(prev => ({
        ...prev,
        [productType]: []
      }));
    } else {
      // Clear all filters
      setCarrierFilter({});
    }
  };

  // Apply carrier filter to quotes
  const getFilteredQuotesByProductType = () => {
    if (!carrierFilter || Object.keys(carrierFilter).length === 0) {
      return quotesByProductType;
    }

    const filtered = {};
    Object.keys(quotesByProductType).forEach(productType => {
      const quotes = quotesByProductType[productType] || [];
      const productTypeFilters = carrierFilter[productType] || [];

      if (productTypeFilters.length === 0) {
        // No filter for this product type, show all quotes
        filtered[productType] = quotes;
      } else {
        // Apply product-specific carrier filter
        filtered[productType] = quotes.filter(quote => {
          const carrierName = quote.carrier_name || quote.carrier || quote.company_name || '';
          const carrierId = quote.carrier_id || quote.carrier || '';

          return productTypeFilters.some(filterId => {
            // carrierFilter contains strings (carrier IDs), so we can safely call toLowerCase
            const filterIdStr = String(filterId || '');
            const carrierNameStr = String(carrierName || '');
            const carrierIdStr = String(carrierId || '');

            return filterIdStr === carrierIdStr || filterIdStr === carrierNameStr || carrierNameStr.toLowerCase().includes(filterIdStr.toLowerCase());
          });
        });
      }
    });
    return filtered;
  };

  // Fuzzy-map HA carriers to ALQ carrier ids based on name matching.
  const mapHaCarriersToAlqIds = haCarriers => {
    if (!Array.isArray(haCarriers) || !Array.isArray(alqCarriers) || alqCarriers.length === 0) {
      return [];
    }

    const normalize = s => {
      if (!s && s !== 0) return '';
      let str = String(s);
      str = str.replace(/([a-z])([A-Z])/g, '$1 $2');
      str = str.replace(/([A-Z]{2,})([A-Z][a-z])/g, '$1 $2');
      return str
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    };

    const normalizeTokens = tokens => {
      const stop = new Set([
        'and',
        'of',
        'the',
        'a',
        'an',
        'company',
        'co',
        'inc',
        'corp',
        'corporation',
        'group',
        'holdings',
        'holding',
        'services',
        'service',
        'financial',
        'finance',
        'fund',
        'funds',
        'life',
        'insurance',
        'assurance',
        'america',
        'american',
        'national'
      ]);

      const stem = t => {
        if (!t) return '';
        if (t === 'co') return 'company';
        if (t === 'inc') return 'incorporated';
        if (t === 'corp') return 'corporation';
        if (t === 'svc') return 'service';
        return t;
      };

      const out = new Set();
      const iterable = tokens && typeof tokens[Symbol.iterator] === 'function' ? tokens : [];
      for (const raw of iterable) {
        const t = stem(String(raw));
        if (!t) continue;
        if (stop.has(t)) continue;
        out.add(t);
      }

      if (out.size === 0) {
        for (const raw of iterable) {
          const t = stem(String(raw));
          if (t) out.add(t);
        }
      }

      return out;
    };

    const buildAcronym = tokens => {
      const stop = new Set(['and', 'of', 'the', 'a', 'an', 'life', 'insurance', 'financial', 'group', 'company', 'co', 'inc', 'corp', 'corporation', 'assurance', 'america', 'american', 'national']);
      return [...tokens]
        .filter(t => t && !stop.has(t))
        .map(t => t[0])
        .join('');
    };

    const normalizeNoSpace = s => normalize(s).replace(/\s+/g, '');

    const alqById = new Map();
    const alqCandidates = [];
    (alqCarriers || []).forEach(c => {
      if (!c) return;
      const id = c.id ?? c.carrier_id ?? c.value;
      const name = (c.name ?? c.carrier_name ?? c.label ?? c.company_name ?? '') || '';
      if (id != null) alqById.set(String(id), id);
      const n = normalize(name);
      const nNoSpace = normalizeNoSpace(name);
      const tokens = normalizeTokens(new Set(n.split(/\s+/).filter(Boolean)));
      alqCandidates.push({ id, name, norm: n, normNoSpace: nNoSpace, tokens, acronym: buildAcronym(tokens) });
    });

    const mappedIds = haCarriers.map(hc => {
      const val = hc && typeof hc === 'object' ? hc.name || hc.label || hc.carrier_name || hc.company_name || hc.id || hc.value : hc;
      const valStr = val == null ? '' : String(val);

      if (alqById.has(valStr)) return alqById.get(valStr);

      const valNorm = normalize(valStr);
      const valNormNoSpace = normalizeNoSpace(valStr);
      const valTokens = normalizeTokens(new Set(valNorm.split(/\s+/).filter(Boolean)));
      const valAcronym = buildAcronym(valTokens);

      if (valNorm || valNormNoSpace) {
        const substringHits = alqCandidates.filter(a => {
          const aNorm = a.norm || '';
          const aNoSpace = a.normNoSpace || '';
          const spaceHit = valNorm ? aNorm.includes(valNorm) || valNorm.includes(aNorm) : false;
          const noSpaceHit = valNormNoSpace ? aNoSpace.includes(valNormNoSpace) || valNormNoSpace.includes(aNoSpace) : false;
          return (aNorm || aNoSpace) && (spaceHit || noSpaceHit);
        });
        if (substringHits.length === 1) return substringHits[0].id;
        if (substringHits.length > 1) {
          substringHits.sort((a, b) => {
            const aLen = (a.normNoSpace || a.norm || '').length;
            const bLen = (b.normNoSpace || b.norm || '').length;
            const vLen = (valNormNoSpace || valNorm || '').length;
            return Math.abs(aLen - vLen) - Math.abs(bLen - vLen);
          });
          return substringHits[0].id;
        }
      }

      if (valTokens.size === 1) {
        const single = [...valTokens][0];
        const candidatesWithToken = alqCandidates.filter(a => a.tokens.has(single));
        if (candidatesWithToken.length > 0) {
          candidatesWithToken.sort((a, b) => {
            let sa = 0;
            let sb = 0;
            if (a.norm.startsWith(single)) sa += 100;
            if (b.norm.startsWith(single)) sb += 100;
            if ((a.name || '').toLowerCase().startsWith(single)) sa += 50;
            if ((b.name || '').toLowerCase().startsWith(single)) sb += 50;
            sa += 10 / Math.max(1, a.norm.split(' ').length);
            sb += 10 / Math.max(1, b.norm.split(' ').length);
            return sb - sa;
          });
          return candidatesWithToken[0].id;
        }
      }

      const exact = alqCandidates.find(a => a.norm === valNorm);
      if (exact) return exact.id;

      if (valTokens.size >= 1) {
        const candidatesContainingAll = alqCandidates.filter(a => {
          if (!a.norm) return false;
          for (const t of valTokens) {
            if (!a.tokens.has(t)) return false;
          }
          return true;
        });

        if (candidatesContainingAll.length === 1) return candidatesContainingAll[0].id;
        if (candidatesContainingAll.length > 1) {
          candidatesContainingAll.sort((a, b) => a.norm.length - b.norm.length);
          return candidatesContainingAll[0].id;
        }
      }

      if (valAcronym && valAcronym.length >= 2) {
        const acronymMatches = alqCandidates.filter(a => a.acronym && a.acronym === valAcronym);
        if (acronymMatches.length === 1) return acronymMatches[0].id;
      }

      let best = null;
      let bestScore = 0;
      let secondBestScore = 0;
      alqCandidates.forEach(a => {
        if (!a.norm) return;

        let score = 0;
        if (a.norm === valNorm) score += 200;
        if (a.norm.startsWith(valNorm)) score += 180;
        else if (valNorm.startsWith(a.norm)) score += 140;
        else if (a.norm.includes(valNorm) || valNorm.includes(a.norm)) score += 80;

        const valTokensArr = [...valTokens];
        let common = 0;
        valTokensArr.forEach(t => {
          if (a.tokens.has(t)) {
            common++;
            return;
          }
          if (t.length >= 4) {
            for (const ct of a.tokens) {
              if (ct.length >= 4 && (ct.startsWith(t) || t.startsWith(ct))) {
                common += 0.5;
                break;
              }
            }
          }
        });
        const extraTokens = Math.max(a.tokens.size - Math.floor(common), 0);
        const unionSize = new Set([...a.tokens, ...valTokens]).size || 1;
        const jaccard = common / unionSize;

        score += common * 35;
        score -= extraTokens * 8;
        score += jaccard * 120;

        if (valTokens.size > 0) {
          let allIn = true;
          for (const t of valTokens) {
            if (!a.tokens.has(t)) {
              allIn = false;
              break;
            }
          }
          if (allIn) score += 60;
        }

        if (valAcronym && a.acronym && valAcronym === a.acronym) score += 50;

        if (score > bestScore) {
          secondBestScore = bestScore;
          bestScore = score;
          best = a;
        } else if (score > secondBestScore) {
          secondBestScore = score;
        }
      });

      if (best && bestScore >= 90 && bestScore >= secondBestScore + 10) {
        return best.id;
      }

      return null;
    });

    const uniqueMappedIds = Array.from(new Set(mappedIds.filter(x => x != null)));

    // Keep only ids that exist in current ALQ carriers.
    const alqIds = new Set((alqCarriers || []).map(c => String(c.id)));
    return uniqueMappedIds.map(id => String(id)).filter(id => alqIds.has(id));
  };

  function handleHealthAnalyzerCarriersChange(haResult) {
    // New contract: haResult is an object from Health Analyzer evaluation,
    // expected shape: { carriers: [...], healthClasses, productIds }.
    const carriers = Array.isArray(haResult?.carriers) ? haResult.carriers : [];
    const healthClasses = Array.isArray(haResult?.healthClasses) ? haResult.healthClasses : [];
    const productIds = Array.isArray(haResult?.productIds) ? haResult.productIds : [];

    const mappedCarrierIds = mapHaCarriersToAlqIds(carriers);

    setHealthAnalyzerResults({
      carrierValues: carriers,
      mappedCarrierIds,
      productIds,
      healthClasses
    });

    // Also reflect HA health classes into underwritingClasses so
    // hasHaResults stays in sync with the latest HA run.
    if (healthClasses.length > 0) {
      setUnderwritingClasses(healthClasses);
    }
  }

  const renderHealthAnalyzerModal = () => {
    if (!openModal || openModal !== 'health-analyzer') return null;

    // Backend sends dates in YYYY-MM-DD format
    // Check multiple sources for the date value
    let dateOfBirth = formData.dateOfBirthLife || quoterConfig?.proposed_insured?.life?.date_of_birth || '';
    let gender = formData.genderLife || quoterConfig?.proposed_insured?.life?.gender || '';
    let jurisdiction = formData.jurisdictionLife || quoterConfig?.proposed_insured?.life?.state || '';
    const faceAmount = (formData.faceAmounts && formData.faceAmounts[0]) || formData.faceAmount || quoterConfig?.quote_data?.face_amounts?.[0] || 0;

    // If still no date, try reading directly from the DOM as last resort
    if (!dateOfBirth) {
      const dobField = document.querySelector('[data-itemid="dob-field"]');
      if (dobField && dobField.value) {
        dateOfBirth = dobField.value;
      }
    }

    if (!gender) {
      // Try to get from UI state if not in formData
      // Look for the selected button using the data-selected attribute (more reliable than checking for .fa-check)
      const selectedGenderBtn = document.querySelector('[data-itemid^="gender-button-"][data-selected="true"]');
      if (selectedGenderBtn) {
        const itemId = selectedGenderBtn.getAttribute('data-itemid');
        if (itemId === 'gender-button-Male') {
          gender = 'Male';
        } else if (itemId === 'gender-button-Female') {
          gender = 'Female';
        }
      }
    }

    // Each time Health Analyzer is (re)opened from the Universal Quoter,
    // clear prior HA state so the new run does not implicitly reuse
    // previous carrier mappings or underwriting classes.
    if (underwritingClasses.length > 0 || (healthAnalyzerResults && (healthAnalyzerResults.mappedCarrierIds?.length || healthAnalyzerResults.carrierValues?.length))) {
      setUnderwritingClasses([]);
      setHealthAnalyzerResults({
        carrierValues: [],
        mappedCarrierIds: [],
        productIds: [],
        healthClasses: []
      });
    }

    // Health Analyzer carrier selection should be driven by the live HA carrier list.
    const availableHaCarriers = Array.isArray(healthAnalyzerCarriers) ? healthAnalyzerCarriers : [];

    return (
      <HealthAnalyzerModal
        areCarriersEqual={true}
        availableCarriers={availableHaCarriers}
        dateOfBirth={dateOfBirth}
        faceAmount={faceAmount}
        gender={gender}
        jurisdiction={jurisdiction}
        onClose={handleModalClose}
        // Always start with no pre-selection; each HA run should be
        // independent and override previous mappings.
        onHaCarriersEvaluated={handleHealthAnalyzerCarriersChange}
        // We no longer rely on this for mapping; keep as a no-op so the
        // modal can call it without breaking, but carrier id mapping is
        // driven by onHaCarriersEvaluated instead.
        selectedCarriers={[]}
        setSelectedCarriers={() => {}}
        // Structured HA evaluation result used to derive mapped ALQ carrier ids.
        setUnderwritingClasses={setUnderwritingClasses}
      />
    );
  };

  // Validation helpers
  const validateCommonFields = (data = formData, tabType) => {
    const dateOfBirthField = tabType === 'annuity' ? data.dateOfBirthAnnuity : data.dateOfBirthLife;
    const jurisdictionField = tabType === 'annuity' ? data.jurisdictionAnnuity : data.jurisdictionLife;

    if (!dateOfBirthField) {
      setQuotesError('Date of birth is required');
      return false;
    }
    if (!jurisdictionField) {
      setQuotesError('Please select a jurisdiction/state');
      return false;
    }
    return true;
  };
  const validateAnnuityFields = (data = formData) => {
    if (!validateCommonFields(data, 'annuity')) return false;
    if (!data.premiumAnnuity || data.premiumAnnuity <= 0) {
      setQuotesError('Please enter a valid premium amount');
      return false;
    }
    if (!data.productTypes || data.productTypes.length === 0) {
      setQuotesError('Please select at least one product type');
      return false;
    }

    return true;
  };

  const validateLifeFields = (data = formData) => {
    if (!validateCommonFields(data, 'life')) return false;
    // Check if both face amount and premium are empty
    const hasFaceAmount = data.faceAmounts && data.faceAmounts.length > 0 && data.faceAmounts.some(fa => fa && parseFloat(fa) > 0);
    const hasPremium = data.premiumLife !== '' && parseFloat(data.premiumLife) > 0;
    if (!data?.healthClass || data?.healthClass?.length === 0) {
      showSnackbar('Please select at least one Health Class', 'error');
      return;
    }
    if (data.solveFor == 'Premium' && !hasFaceAmount) {
      setQuotesError('Please enter at least one face amount');
      return false;
    }
    if (data.solveFor == 'Face Amount' && !hasPremium) {
      setQuotesError('Please enter a premium amount');
      return false;
    }
    if (!data.selectedProductTypes || data.selectedProductTypes.length === 0) {
      setQuotesError('Please select at least one product');
      return false;
    }
    // Check if IUL tab is active (not just if IUL products are selected)
    const isIULTabActive = data.activeProductTab === 'IUL';

    if (isIULTabActive && !data.death_benefit_option) {
      setQuotesError('Please select a death benefit option (Level or Increasing) for IUL products');
      return false;
    }
    // Validate IUL premium fields only when IUL tab is active
    if (isIULTabActive) {
      if (!data.annual_premium_iul || parseFloat(data.annual_premium_iul) <= 0) {
        setQuotesError('Please enter a valid annual premium amount for IUL products');
        return false;
      }
      if (!data.to_age_iul || parseInt(data.to_age_iul) <= 0) {
        setQuotesError('Please enter a valid "to age" value for IUL products');
        return false;
      }
      // Validate initial premium if provided
      if (data.initial_premium_iul && parseFloat(data.initial_premium_iul) < 0) {
        setQuotesError('Initial premium must be a positive value');
        return false;
      }
      // Validate one-time premium if provided
      if (data.one_time_premium_iul && parseFloat(data.one_time_premium_iul) < 0) {
        setQuotesError('One-time premium must be a positive value');
        return false;
      }
      // Validate face amount type
      if (!data.face_amount_type_iul) {
        setQuotesError('Please select a face amount type (Minimum, Maximum, or Specified) for IUL products');
        return false;
      }
      // Validate specified face amount if 'Specified' is selected
      if (data.face_amount_type_iul === 'Specified') {
        if (!data.specified_face_amount_iul || parseFloat(data.specified_face_amount_iul) <= 0) {
          setQuotesError('Please enter a valid specified face amount for IUL products');
          return false;
        }
      }
    }
    return true;
  };
  // Common setup for both quote types
  const setupQuoteRequest = productTypesArray => {
    setCurrentView('loading');
    setQuotesError(null);
    setAppliedFilters(null);
    setHasInitiatedQuoteRequest(true); // Mark that we've initiated at least one quote request

    // Reset loading states
    const newLoadingStates = { ...loadingStates };
    productTypesArray.forEach(productType => {
      newLoadingStates[productType] = true;
    });
    setLoadingStates(newLoadingStates);

    // Clear previous quotes
    const newQuotesByProductType = { ...quotesByProductType };
    const newOriginalQuotesByProductType = { ...originalQuotesByProductType };
    productTypesArray.forEach(productType => {
      newQuotesByProductType[productType] = [];
      newOriginalQuotesByProductType[productType] = [];
    });
    setQuotesByProductType(newQuotesByProductType);
    setOriginalQuotesByProductType(newOriginalQuotesByProductType);
  };

  // Update quotes helper
  const updateQuotesForProductType = (productType, quotes) => {
    setQuotesByProductType(prev => ({
      ...prev,
      [productType]: quotes
    }));
    setOriginalQuotesByProductType(prev => ({
      ...prev,
      [productType]: quotes
    }));
  };

  // Common error handler
  const handleQuoteError = (error, quoteType) => {
    console.error(`Error fetching ${quoteType} quotes:`, error);
    setQuotesError(error.message || `Failed to fetch ${quoteType} quotes`);
    setCurrentView('form');
  };

  // Overlay form data management
  const openQuoteSetupOverlay = () => {
    // Copy current formData to overlay state when opening
    setOverlayFormData({ ...formData });
    setIsQuoteSetupOverlayOpen(true);
  };

  const closeQuoteSetupOverlay = () => {
    setIsQuoteSetupOverlayOpen(false);
    // Don't apply overlay changes - just close
    setOverlayFormData(null);
  };

  const applyOverlayChanges = () => {
    // Apply overlay changes to main form data
    if (overlayFormData) {
      setFormData({ ...overlayFormData });
    }
    setIsQuoteSetupOverlayOpen(false);
    setOverlayFormData(null);
  };

  // Handle input changes for overlay form
  const handleOverlayInputChange = (name, value) => {
    setOverlayFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async e => {
    // Prevent default form submission if event is provided
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    // Determine which data to use BEFORE making any state changes
    const dataToSubmit = isQuoteSetupOverlayOpen && overlayFormData ? overlayFormData : formData;

    // If submitting from overlay, apply changes to main form data
    if (isQuoteSetupOverlayOpen && overlayFormData) {
      setFormData({ ...overlayFormData });
      setOverlayFormData(null);
    }

    // Close the quote setup overlay if it's open
    setIsQuoteSetupOverlayOpen(false);

    if (activeTab === 'new-annuity') {
      if (!validateAnnuityFields(dataToSubmit)) return;

      setupQuoteRequest(dataToSubmit.productTypes);

      try {
        const annuityQuoter = uqData?.universal_quoter_details?.find(detail => detail.quoter_type === 1);
        const quoterTokenId = annuityQuoter?.quoter_token_id;

        if (!quoterTokenId) {
          throw new Error('Annuity quoter token not found');
        } // Prepare API requests
        const requests = dataToSubmit.productTypes.map(productType => ({
          params: buildAnnuityQuotesPayload(productType, dataToSubmit),
          options: { universal: true, id: quoterTokenId },
          productType
        }));

        const results = await AnnuitiesApi.getMultipleQuotes(requests);

        // Process results and update states
        let hasSuccessfulResults = false;
        const errorMessages = [];
        const collectedAnnuityQuotes = [];

        results.forEach((result, index) => {
          const productType = requests[index].productType;

          if (result.status === 'fulfilled' && !result.value.error) {
            const quotesData = result.value || [];
            updateQuotesForProductType(productType, quotesData);
            collectedAnnuityQuotes.push(...quotesData);
            hasSuccessfulResults = true;
          } else {
            const errorMessage = result.value?.error || result.reason?.message || 'Unknown error';
            console.error(`Error fetching ${productType} quotes:`, errorMessage);
            errorMessages.push(`${productType}: ${errorMessage}`);

            // Still update with empty array for failed product types
            updateQuotesForProductType(productType, []);
          }

          // Always update loading state to false for this product type
          setLoadingStates(prev => ({
            ...prev,
            [productType]: false
          }));
        });

        if (!hasSuccessfulResults) {
          throw new Error(`All product type requests failed: ${errorMessages.join('; ')}`);
        }

        // Populate UQ_EMBED_CONFIG_QUOTE_ACTIONS for annuity quotes so the
        // hasQuoteValidActions utility (and Start eApp button) can evaluate
        // embed config actions (e.g. fl_apply) against the fetched quotes,
        // exactly as the life flow does via fetchLifeQuotesParallel.
        if (collectedAnnuityQuotes.length) {
          // Add embed config quote actions for annuity quotes
          if (QUOTER_CONFIG?.quote_actions && QUOTER_CONFIG?.quote_actions?.length) {
            const allQuotes = Object.values(collectedAnnuityQuotes).flat();

            if (QUOTER_CONFIG?.embed_context === 'fire_light') {
              const productIds = [...new Set(allQuotes.map(q => q?.product_id))];
              if (productIds?.length > 0) {
                try {
                  const productsResponse = await AnnuitiesApi.loadProduct({ id: productIds });
                  const quotesWithCusips = addCusipsToQuotes(productsResponse, allQuotes);
                  addEmbedConfigQuoteActions(quotesWithCusips);
                } catch (err) {
                  console.warn('Failed to load products for CUSIP data:', err);
                  addEmbedConfigQuoteActions(allQuotes);
                }
              }
            } else {
              addEmbedConfigQuoteActions(allQuotes);
            }
          } else {
            addEmbedConfigQuoteActions(collectedAnnuityQuotes);
          }
        }

        // Clear compare selections
        setCompareSelections({});

        setCurrentView('results');
      } catch (error) {
        handleQuoteError(error, 'Annuity');
      }
    } else if (activeTab === 'new-life') {
      fetchLifeQuotesParallel(dataToSubmit);
      return;
    }
  };

  // Parallel variant: fires all face-amount AlqApi calls simultaneously
  // via Promise.allSettled and merges every result into a single state update.
  const fetchLifeQuotesParallel = async dataToSubmit => {
    if (!validateLifeFields(dataToSubmit)) return;

    const lifeQuoter = uqData?.universal_quoter_details?.find(detail => detail.quoter_type === 2);
    const quoterTokenId = lifeQuoter?.quoter_token_id;

    if (!quoterTokenId) {
      setQuotesError('Life quoter token not found');
      return;
    }

    const hasHaResults = Array.isArray(underwritingClasses) && underwritingClasses.length > 0;

    setupQuoteRequest(dataToSubmit.selectedProductTypes);

    try {
      const preparedFormData = { ...dataToSubmit };

      if (preparedFormData) {
        if (hasHaResults) {
          const haMapped = Array.isArray(healthAnalyzerResults?.mappedCarrierIds) ? healthAnalyzerResults.mappedCarrierIds.map(id => parseInt(id, 10)) : [];
          preparedFormData.selectedCarriers = haMapped.length > 0 ? haMapped : [];
        } else if (!Array.isArray(dataToSubmit?.selectedCarriers) || dataToSubmit.selectedCarriers.length === 0) {
          preparedFormData.selectedCarriers = alqCarriers?.map(c => c.id) || [];
        }
      }

      const isFaceSolve = dataToSubmit?.solveFor === 'Face Amount' || dataToSubmit?.solveFor === 'Face';
      const requestConfigs = isFaceSolve
        ? [
            {
              requestLabel: `premium ${dataToSubmit?.premiumLife}`,
              payload: buildLifeQuotesPayload(null, dataToSubmit?.premiumLife, preparedFormData)
            }
          ]
        : (dataToSubmit?.faceAmounts || []).map(faceAmount => ({
            requestLabel: `face amount ${faceAmount}`,
            payload: buildLifeQuotesPayload(faceAmount, dataToSubmit?.premiumLife, preparedFormData)
          }));

      // Build one request for Face solves from the premium input,
      // or one per face amount for Premium solves.
      const requests = requestConfigs.map(({ payload }) => AlqApi.getQuotes(payload, { universal: true, id: quoterTokenId }));

      const results = await Promise.allSettled(requests);

      // Collect and merge all successful results
      const collectedQuotes = {};
      const requestedProductTypes = dataToSubmit.selectedProductTypes || [];

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const quotesArray = result.value?.data || result.value || [];
          if (Array.isArray(quotesArray)) {
            quotesArray.forEach(quote => {
              // The engine echoes the requested product type on each quote, so bucket by it to keep
              // results under the same tab the user selected (e.g. "Final Expense", "To age 121 (No Lapse U/L)").
              const productType = requestedProductTypes.includes(quote?.product_type) ? quote.product_type : determineLifeProductType(quote?.product_name, quote?.product_type);
              if (productType) {
                if (!collectedQuotes[productType]) {
                  collectedQuotes[productType] = [];
                }
                collectedQuotes[productType].push(quote);
              }
            });
          }
        } else {
          console.error(`Life Quoter api call failed for ${requestConfigs[index]?.requestLabel || 'request'}:`, result.reason);
        }
      });

      // Single state update with all collected quotes
      setQuotesByProductType({ ...collectedQuotes });
      setOriginalQuotesByProductType({ ...collectedQuotes });
      setCompareSelections({});

      // Add embed config quote actions for life quotes
      if (QUOTER_CONFIG.quote_actions && QUOTER_CONFIG.quote_actions.length) {
        const allQuotes = Object.values(collectedQuotes).flat();

        if (QUOTER_CONFIG.embed_context === 'fire_light') {
          const productIds = [...new Set(allQuotes.map(q => q.product_id))];
          if (productIds.length > 0) {
            try {
              const productsResponse = await AlqApi.loadProducts(productIds);
              const quotesWithCusips = addCusipsToQuotes(productsResponse.data, allQuotes);
              addEmbedConfigQuoteActions(quotesWithCusips);
            } catch (err) {
              console.warn('Failed to load products for CUSIP data:', err);
              addEmbedConfigQuoteActions(allQuotes);
            }
          }
        } else {
          addEmbedConfigQuoteActions(allQuotes);
        }
      }

      // Turn off all loading spinners in one update
      const finalLoadingUpdates = {};
      (dataToSubmit.selectedProductTypes || []).forEach(productType => {
        finalLoadingUpdates[productType] = false;
      });
      setLoadingStates(prev => ({ ...prev, ...finalLoadingUpdates }));

      setCurrentView('results');
    } catch (error) {
      handleQuoteError(error, 'life');
    }
  };

  // Handle share modal close
  const handleShareModalClose = () => {
    setIsShareModelOpen(false);
    setShareModelData(null);
  };

  // Handle share action from header
  const handleShareAction = selections => {
    const totalSelected = Object.values(selections).reduce((total, productSelections) => total + productSelections.length, 0);

    if (totalSelected === 0) {
      alert('Please select quotes to share by checking the checkboxes in the results table.');
      return;
    }

    if (totalSelected > 1) {
      alert('Please select only 1 quote to share');
      return;
    }

    // Create sharing data from selections
    const sharingData = Object.entries(selections)
      .filter(([_, productSelections]) => productSelections.length > 0)
      .map(([productType, productSelections]) => ({
        productType,
        selectedQuotes: productSelections
          .map(quoteId => {
            const quotes = quotesByProductType[productType] || [];
            return quotes.find(quote => quote.id === quoteId);
          })
          .filter(Boolean)
      }))
      .filter(({ selectedQuotes }) => selectedQuotes.length > 0);

    if (sharingData.length === 0) {
      alert('No valid quotes found for sharing.');
      return;
    }
    // Flatten all selected quotes
    const selectedQuotes = sharingData.reduce((acc, { selectedQuotes }) => [...acc, ...selectedQuotes], []);

    if (selectedQuotes?.length !== 1) {
      alert('Unable to find selected quotes for sharing.');
      return;
    }

    setShareModelData({
      quotes: activeTab === 'new-life' ? selectedQuotes : selectedQuotes.map(quote => quote.id),
      formData,
      selectedQuotes: selectedQuotes.map(q => q.guid)
    });
    setIsShareModelOpen(true);
  };

  // Handle comparison action from header
  const handleCompareAction = selections => {
    const totalSelected = Object.values(selections).reduce((total, productSelections) => total + productSelections.length, 0);

    if (totalSelected === 0) {
      alert('Please select quotes to compare by checking the checkboxes in the results table.');
      return;
    }

    if (totalSelected < 2) {
      alert('Please select at least 2 quotes to compare');
      return;
    }

    // Create comparison data from selections
    const comparisonData = Object.entries(selections)
      .filter(([_, productSelections]) => productSelections.length > 0)
      .map(([productType, productSelections]) => ({
        productType,
        selectedQuotes: productSelections
          .map(quoteId => {
            const quotes = quotesByProductType[productType] || [];
            return quotes.find(quote => quote.id === quoteId);
          })
          .filter(Boolean)
      }))
      .filter(({ selectedQuotes }) => selectedQuotes.length > 0);

    if (comparisonData.length === 0) {
      alert('No valid quotes found for comparison.');
      return;
    }

    // Flatten all selected quotes
    const selectedQuotes = comparisonData.reduce((acc, { selectedQuotes }) => [...acc, ...selectedQuotes], []);

    if (selectedQuotes.length < 2) {
      alert('Unable to find selected quotes for comparison');
      return;
    }

    // Set modal data and open appropriate modal
    setCompareModalData({
      quotes: selectedQuotes,
      activeTab,
      formData,
      productFeatures: activeTab === 'new-life' ? alqProductFeatures?.data : aaqProductsFeatures,
      selectedQuotes: selectedQuotes.map(q => q.guid)
    });
    setIsCompareModalOpen(true);
  };
  const buildAnnuityQuotesPayload = (productType, data = formData) => {
    const carrierIds = data.selectedCarriers.map(carrier => {
      // Handle both object format {id: xxx, name: 'yyy'} and direct ID format
      if (typeof carrier === 'object' && carrier.id) {
        return carrier.id;
      }
      return carrier;
    });

    // Map product type from form to API format
    const productTypeMapping = {
      FA: 'Fixed Annuity',
      MYGA: 'MYGA',
      FIA: 'Fixed Indexed Annuity'
    };

    const productTypes = [productTypeMapping[productType] || productType];
    const payload = {
      first_name: data.proposedInsuredAnnuityFirstName || '',
      last_name: data.proposedInsuredAnnuityLastName || '',
      date_of_birth: data.dateOfBirthAnnuity || '1974-07-29', // Default if not provided
      gender: data.genderAnnuity || 'Male',
      state: data.jurisdictionAnnuity || 'AL',
      agent: {
        first_name: data.firstName || '',
        last_name: data.lastName || '',
        email: data.agentEmail || '',
        phone: data.agentPhone || '',
        insurance_license_number: data.agentLicenseNumber || ''
      },
      product_types: productTypes,
      carrier_ids: carrierIds.length > 0 ? carrierIds : getDefaultCarrierIds(),
      am_best_rating: data.amBestRating || ['Any'],
      surrender_charge_period: data.surrenderChargePeriod || '',
      guaranteed_period: data.guaranteedPeriod || ['0'],
      min_initial_rate: data.minInitialRate || '0.0',
      bonus_rate: data.bonusRate || false,
      bonus_exc: data.bonusExc || false,
      taxTreatment: data.taxTreatment || 'Non-Qualified',
      features: data.features || [],
      gmb_rider: data.gmbRider || false,
      index_id: data.indexId || '',
      solve_for: data.solveFor || 'Accumulation',
      premium: parseFloat(data.premiumAnnuity) || 150000,
      start_year_offset: data.startYearOffset || 0,
      gmb_start_year: data.gmbStartYear || 0,
      quote_value: data.quoteValue || '5',
      enforce_rmds: formData.enforceRmds || false,
      product_ids: formData.productIds || [],
      display_value_at_year: parseInt(formData.displayValueAtYear) || 5
    };
    // Only include new annuity income quote fields for FIA quotes
    if (productType === 'FIA') {
      // If rider income is selected, then pass GMB elected as true. If other option is selected, GMB elected as false
      payload.gmb_elected = !!(data.gmbElected || data?.solveFor === 'Income');
      payload.gmb_rider = !!(data.gmbRider || data?.solveFor === 'Income');
      payload.income_type = data.incomeType || data?.solveFor === 'Income' ? 'Single' : null;
      payload.joint_covered_life_dob = data.jointCoveredLifeDOB || null;
    }

    return payload;
  };

  // Get default carrier IDs when none are selected
  const getDefaultCarrierIds = () => {
    const availableCarriers = getAvailableCarriers();
    if (availableCarriers && availableCarriers.length > 0) {
      return availableCarriers.slice(0, 10).map(carrier => carrier.id); // Use first 10 carriers as default
    }

    // Fallback to some default carrier IDs if no carriers are loaded
    return [
      1812, 1827, 1831, 1821, 1851, 1855, 1875, 1791, 1505, 1829, 1814, 1770, 1574, 1673, 106, 76, 1815, 138, 27, 139, 1852, 1602, 99, 109, 1832, 1826, 69, 1871, 1817, 1634, 1860, 1641, 93, 1856,
      1798, 1823, 1835, 1865
    ];
  };
  // Build payload for life quotes
  const buildLifeQuotesPayload = (faceAmount = null, premium = null, data = formData) => {
    const carrierIds = data.selectedCarriers.map(carrier => {
      if (typeof carrier === 'object' && carrier.id) {
        return carrier.id;
      }
      return carrier;
    });

    let apiSolveFor;
    if (data?.solveFor === 'Face Amount' || data?.solveFor === 'Face') {
      apiSolveFor = 'Face';
    } else {
      apiSolveFor = 'Premium';
    }

    const payload = {
      first_name: data.proposedInsuredLifeFirstName || '',
      last_name: data.proposedInsuredLifeLastName || '',
      date_of_birth: data.dateOfBirthLife || '07-29-1975',
      gender: data.genderLife || 'Male',
      product_limits: lifeProductOptions.product_limits || 3,
      enable_quote_by_premium: alqQuoter?.enable_quote_by_premium || false,
      tobacco: data.tobacco || false,
      state: data.jurisdictionLife || 'CO',
      visible_health_type: 'classes',
      health_categories: data.healthClass || ['Preferred Plus'],
      flat_extra: '0.00',
      carrier_ids: carrierIds.length > 0 ? carrierIds : alqCarriers?.map(c => c.id) || [],
      product_types: data.selectedProductTypes || [],
      child_rider_units: String(data.child_rider_units) || '0',
      product_features: data.features || [],
      product_ids: [],
      adb_rider: data.adb_rider || false,
      wop_rider: data.wop_rider || false,
      solve_for: apiSolveFor,
      client: {
        first_name: '',
        last_name: ''
      },
      agent: {
        first_name: data.firstName || '',
        last_name: data.lastName || '',
        email: data.agentEmail || '',
        phone: data.agentPhone || '',
        insurance_license_number: data.agentLicenseNumber || ''
      },
      vive_score_enabled: alqQuoter?.vive_score_enabled || false
    };

    payload.premium_modes = [data.premiumMode ? data.premiumMode.toLowerCase() : 'annual'];

    if (apiSolveFor === 'Face') {
      // Solving for Face Amount - send premium
      if (premium !== null && premium !== undefined && parseFloat(premium) > 0) {
        payload.premium = parseFloat(premium);
      } else if (data.premiumLife && parseFloat(data.premiumLife) > 0) {
        payload.premium = parseFloat(data.premiumLife);
      }
    } else {
      // Solving for Premium - send face_amount
      if (faceAmount !== null && faceAmount !== undefined && parseFloat(faceAmount) > 0) {
        payload.face_amount = parseFloat(faceAmount);
      } else if (data.faceAmounts?.[0] && parseFloat(data.faceAmounts[0]) > 0) {
        payload.face_amount = parseFloat(data.faceAmounts[0]);
      }
    }

    return payload;
  };

  // Determine which product type a life quote belongs to based on product name or product type
  const determineLifeProductType = (productName, productType) => {
    // Use product_type field first if available, then fall back to product_name
    const typeToCheck = productType || productName;
    if (!typeToCheck) return 'Other';

    const productLower = typeToCheck.toLowerCase(); // Handle direct API product_type mappings first
    if (productType) {
      // Check for IUL in product_type field (including parentheses format like "To age 65 ( IUL )")
      if (productType.includes('( IUL )') || productType.includes('(IUL)')) {
        return productType; // Return as-is to preserve the full product type name like "To age 65 ( IUL )"
      }

      // Direct mapping for common API product types
      switch (productType) {
        case 'To age 80':
          return 'To age 80';
        case 'To age 85':
          return 'To age 85';
        case 'To age 90':
          return 'To age 90';
        case 'To age 95':
          return 'To age 95';
        case 'To age 100':
          return 'To age 100';
        case 'To age 121':
          return 'To age 121';
        case 'Whole Life':
          return 'Whole Life';
        case 'Final Expense':
          return 'Final Expense';
        case 'Universal Life':
          return 'Universal Life';
        case 'Variable Universal Life':
          return 'Variable Universal Life';
        case 'Term Life':
          return 'Term Life';
        // Handle specific term years
        default:
          if (productType.includes('Year Term')) {
            return productType; // Return as-is for "10 Year Term", "20 Year Term", etc.
          }
          if (productType.includes('Year ROP')) {
            return productType; // Return as-is for "20 Year ROP", etc.
          }
      }
    } // Fallback to name-based detection for cases where product_type is not specific enough
    // Check for IUL products FIRST (before other checks to avoid misclassification)
    if (productLower.includes('iul') || productLower.includes('i u l') || productLower.includes('indexed universal')) {
      return 'IUL';
    }

    // Check for specific term years first (more specific matching)
    const termYearMatch = productLower.match(/(\d+)\s*year\s*term/i);
    if (termYearMatch) {
      const termYear = termYearMatch[1];

      // Check if it's ROP term
      if (productLower.includes('rop') || productLower.includes('return of premium')) {
        return `${termYear} Year ROP`;
      }

      // Regular term
      return `${termYear} Year Term`;
    }

    // Check for age-based GUL products (but NOT if it contains IUL)
    const ageMatch = productLower.match(/to\s*age\s*(\d+)/i);
    if (ageMatch && !productLower.includes('iul') && !productLower.includes('i u l')) {
      const age = ageMatch[1];
      return `To age ${age}`;
    }

    // Check for general term products (without specific year)
    if (productLower.includes('term') && !productLower.includes('rop')) {
      return 'Term Life';
    }

    // Check for ROP products
    if (productLower.includes('rop') || productLower.includes('return of premium')) {
      return 'ROP';
    }

    // Check for guaranteed/universal life products
    if (productLower.includes('no lapse') || productLower.includes('pay to') || productLower.includes('guaranteed') || productLower.includes('gul')) {
      return 'GUL';
    }

    // Final Expense is its own selectable product type; keep it out of the Whole Life bucket
    if (productLower.includes('final expense')) {
      return 'Final Expense';
    }

    // Check for whole life products
    if (productLower.includes('whole life')) {
      return 'Whole Life';
    }

    // Check for universal life products
    if (productLower.includes('universal life')) {
      return 'Universal Life';
    }

    // Check for variable products
    if (productLower.includes('variable')) {
      return 'Variable Universal Life';
    }

    return 'Other'; // Default fallback
  };

  // Handle filter application
  const handleFiltersApply = filters => {
    setAppliedFilters(filters);

    if (activeTab === 'new-life') {
      applyLifeFilters(filters);
    } else if (activeTab === 'new-annuity') {
      applyAnnuityFilters(filters);
    }
  };

  // Clear all filters and restore original quotes
  const handleFiltersClear = () => {
    setAppliedFilters(null);
    setCarrierFilter({}); // Clear carrier filter as well

    // Restore original quotes
    setQuotesByProductType({ ...originalQuotesByProductType });
  };

  function transformValues(obj, replaceMap) {
    // Deep, immutable clone + transform
    if (Array.isArray(obj)) {
      return obj.map(v => transformValues(v, replaceMap));
    }
    if (obj && typeof obj === 'object') {
      return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, transformValues(v, replaceMap)]));
    }
    if (typeof obj === 'string') {
      return replaceMap.get(obj) ?? obj; // replace if in map
    }
    return obj; // pass-through for other types
  }

  // Apply filters to life quotes
  const applyLifeFilters = filters => {
    const filteredQuotes = {};

    const replacements = new Map([
      ['Increase Face Amount', 'Option to Increase Face Amount'],
      ['Decrease Face Amount', 'Option to Decrease Face Amount'],
      ['Convertability', 'Convertible'],
      ['Terminal Illness', 'Terminal Illness Covered (ADB Rider)'],
      ['Chronic Illness', 'Chronic Illness Covered (ADB Rider)'],
      ['Critical Illness', 'Critical Illness Covered (ADB Rider)'],
      ['Common Carrier Accidental Death Benefit', 'Included Common Carrier Accidental Death Benefit Rider'],
      ['Cost of Living Increase', 'Included Cost of Living Increase Rider'],
      ['Family Health Benefit', 'Included Family Health Benefit Rider'],
      ['Nursing Home Confinement', 'Included Nursing Home Confinement Rider'],
      ['Waiver of Premium for Unemployment', 'Included Unemployment Waiver of Premium'],
      ['Residential Damage Benefit', 'Included Residential Damage Benefit'],
      ['Accelerated Underwriting', 'Carrier has an Accelerated Underwriting Program'],
      ['Non Medical (Simplified Issue)', 'Non-Medical (Simplified Issue)'],
      ['Discount Program', 'Discount Program Enrollment'],
      ['Firelight eSubmission', 'e_app']
    ]);

    const filtersMain = { ...filters };
    const healthClass = filters?.healthClass || [];
    delete filtersMain.healthClass;
    const amBestRating = filtersMain?.amBestRating || '';
    delete filtersMain.amBestRating;

    const filtersTransforms = transformValues(filtersMain, replacements);
    const filtersFlatArray = Object.values(filtersTransforms).flatMap(value => (Array.isArray(value) ? value : [value]));

    // Process each product type
    Object.keys(originalQuotesByProductType).forEach(productType => {
      const originalQuotes = originalQuotesByProductType[productType] || [];
      if (originalQuotes.length === 0) {
        filteredQuotes[productType] = [];
        return;
      }

      let filtered = [...originalQuotes];

      if (healthClass.length > 0) {
        filtered = filtered.filter(item => healthClass.includes(item?.ixn_health_category));
      }

      if (filtersFlatArray.length > 0) {
        filtered = filtered.filter(quote => {
          const product_features = quote?.product_features || [];
          return filtersFlatArray.every(feature => product_features.includes(feature));
        });
      }

      if (amBestRating && amBestRating !== '') {
        const selectedRating = amBestRating;

        filtered = filtered.filter(quote => {
          const quoteRating = quote.am_best_rating;

          // Handle "NA" selection - show only quotes with null ratings
          if (selectedRating === 'NA') {
            return quoteRating === null || quoteRating === undefined || quoteRating === '';
          }

          // For actual ratings, exclude null ratings and apply meet-or-exceed logic
          if (!quoteRating || quoteRating === null || quoteRating === undefined || quoteRating === '') {
            return false;
          }

          const selectedRatingValue = AMBEST_SORTING[selectedRating] || 0;
          const quoteRatingValue = AMBEST_SORTING[quoteRating] || 0;

          // Include quotes that meet or exceed the selected rating
          return quoteRatingValue >= selectedRatingValue;
        });
      }

      filteredQuotes[productType] = filtered;
    });

    setQuotesByProductType(filteredQuotes);
  };

  // Apply filters to annuity quotes
  const applyAnnuityFilters = filters => {
    const filteredQuotes = {};

    // Process each product type
    Object.keys(originalQuotesByProductType).forEach(productType => {
      const originalQuotes = originalQuotesByProductType[productType] || [];

      if (originalQuotes.length === 0) {
        filteredQuotes[productType] = [];
        return;
      }

      let filtered = [...originalQuotes];

      // Apply riders filters
      if (filters.riders) {
        // Apply Living Benefit Rider filter
        if (filters.riders.livingBenefitRider === true) {
          filtered = filtered.filter(quote => {
            const featureNames = quote.feature_names || [];
            const riderName = quote.rider_name || '';
            const productName = (quote.product_name || '').toLowerCase();

            return (
              featureNames.some(feature => feature.toLowerCase().includes('living benefit') || feature.toLowerCase().includes('gmwb') || feature.toLowerCase().includes('withdrawal benefit')) ||
              riderName.toLowerCase().includes('gmwb') ||
              riderName.toLowerCase().includes('living benefit') ||
              productName.includes('gmwb') ||
              productName.includes('living benefit')
            );
          });
        }

        // Apply Age-Based Rate filter
        const ageRateFilter = filters.riders.ageRate;
        if (ageRateFilter && ageRateFilter.age !== '' && ageRateFilter.rate !== '') {
          const selectedAge = parseInt(ageRateFilter.age, 10);
          const minimumRate = parseFloat(ageRateFilter.rate);

          filtered = filtered.filter(quote => {
            const ageBandSingleMap = quote.age_band_single_map || {};
            if (ageBandSingleMap[selectedAge] !== undefined) {
              const rateAtAge = parseFloat(ageBandSingleMap[selectedAge]) || 0;
              return rateAtAge >= minimumRate;
            }

            // If age not found in either map, exclude the quote
            return false;
          });
        }
      }

      // Apply surrender charge filters
      if (filters.surrenderCharge && filters.surrenderCharge.length > 0) {
        filtered = filtered.filter(quote => {
          const surrenderPeriod = parseInt(quote.surrender_charge || 0);

          return filters.surrenderCharge.some(period => {
            if (period === '10+ years') {
              return surrenderPeriod >= 10;
            } else {
              const periodNumber = parseInt(period.replace(/[^\d]/g, ''));
              return surrenderPeriod === periodNumber;
            }
          });
        });
      }

      // Apply guarantee period filters
      if (filters.guaranteePeriod && filters.guaranteePeriod.length > 0) {
        filtered = filtered.filter(quote => {
          const guaranteePeriod = parseInt(quote.gtd_period || 0);

          return filters.guaranteePeriod.some(period => {
            if (period === '10+ years') {
              return guaranteePeriod >= 10;
            } else {
              const periodNumber = parseInt(period.replace(/[^\d]/g, ''));
              return guaranteePeriod === periodNumber;
            }
          });
        });
      }

      // ADD AM BEST RATING FILTER
      if (filters.amBestRating && filters.amBestRating !== '') {
        const selectedRating = filters.amBestRating;

        filtered = filtered.filter(quote => {
          const quoteRating = quote.am_best_rating;

          // Handle "NA" selection - show only quotes with null/undefined/empty ratings
          if (selectedRating === 'NA') {
            return quoteRating === null || quoteRating === undefined || quoteRating === '';
          }

          // For actual ratings (A++, A+, etc.), show quotes that meet or exceed
          if (!quoteRating || quoteRating === null || quoteRating === undefined || quoteRating === '') {
            return false;
          }

          const selectedRatingValue = AMBEST_SORTING[selectedRating] || 0;
          const quoteRatingValue = AMBEST_SORTING[quoteRating] || 0;

          return quoteRatingValue >= selectedRatingValue;
        });
      }

      // Apply MVA filter
      if (filters.mva === true) {
        filtered = filtered.filter(quote => {
          const featureNames = quote.feature_names || [];
          const productName = (quote.product_name || '').toLowerCase();

          return (
            featureNames.some(feature => feature.toLowerCase().includes('market value adjustment') || feature.toLowerCase().includes('mva')) ||
            productName.includes('mva') ||
            productName.includes('market value adjustment')
          );
        });
      }

      // Apply Rates filters
      if (filters.rates) {
        // Apply Minimum Guarantee Percent filter
        if (filters.rates.minimumGuaranteePercent !== null && filters.rates.minimumGuaranteePercent !== undefined && filters.rates.minimumGuaranteePercent !== '') {
          filtered = filtered.filter(quote => {
            const guaranteeRate = parseFloat(quote.base_rate || 0);
            const minGuaranteePercent = parseFloat(filters.rates.minimumGuaranteePercent);
            return guaranteeRate >= minGuaranteePercent;
          });
        }

        // Apply Minimum Current Rate filter
        if (filters.rates.minimumCurrentRate !== null && filters.rates.minimumCurrentRate !== undefined && filters.rates.minimumCurrentRate !== '') {
          filtered = filtered.filter(quote => {
            const currentRate = parseFloat(quote.current_rate || 0);
            const minCurrentRate = parseFloat(filters.rates.minimumCurrentRate);
            return currentRate >= minCurrentRate;
          });
        }
      }

      if (filters.premiumType && filters.premiumType.length > 0) {
        filtered = filtered.filter(quote => {
          const quotePremiumType = (quote.premium_type || '').toLowerCase();

          return filters.premiumType.some(selectedType => {
            const selectedTypeLower = selectedType.toLowerCase();
            return quotePremiumType.includes(selectedTypeLower);
          });
        });
      }
      // Apply Available Indexes filter (for FIA products only)
      if (filters.availableIndexes && filters.availableIndexes.length > 0) {
        // Only apply this filter to FIA product types
        if (productType === 'FIA') {
          filtered = filtered.filter(quote => {
            const quoteIndexName = (quote.index_name || '').toLowerCase();

            // If quote has no index_name, exclude it from FIA results when index filter is applied
            if (!quoteIndexName) {
              return false;
            }

            // For now, we'll do a simple match - this might need to be updated based on
            // how the index IDs (2, 3) correspond to index names
            return filters.availableIndexes.some(selectedIndexId => {
              // Convert index ID to string for comparison
              const indexIdStr = selectedIndexId.toString();

              // Match by ID as string or check if index name contains common index names
              // This is a simplified approach - you might need to map the IDs to actual names
              if (indexIdStr === '2') {
                return quoteIndexName.includes('spx') || quoteIndexName.includes('s&p') || quoteIndexName.includes('500');
              } else if (indexIdStr === '3') {
                return quoteIndexName.includes('nasdaq') || quoteIndexName.includes('ndx');
              }

              // Fallback: direct string comparison
              return quoteIndexName.includes(indexIdStr);
            });
          });
        }
        // For non-FIA products, don't apply index filtering (let them pass through)
      }

      filteredQuotes[productType] = filtered;
    });

    setQuotesByProductType(filteredQuotes);
  }; // Helper to check if quotes have been loaded at least once (vs initial state)
  const hasQuotesBeenLoaded = () => {
    return hasInitiatedQuoteRequest;
  };

  // Get current quotes data for the active tab (used for filter enabling)
  const getCurrentQuotesData = () => {
    if (activeTab === 'new-life') {
      // Return all life quotes as a flat array for filter enabling
      return Object.values(quotesByProductType)
        .filter(quotes => Array.isArray(quotes))
        .flat();
    } else if (activeTab === 'new-annuity') {
      // Return all annuity quotes as a flat array for filter enabling
      return Object.values(quotesByProductType)
        .filter(quotes => Array.isArray(quotes))
        .flat();
    }
    return [];
  };

  // Get original quotes data for the active tab (used for enabling clear filters even when no filtered results)
  const getOriginalQuotesData = () => {
    if (activeTab === 'new-life') {
      // Return all original life quotes as a flat array
      return Object.values(originalQuotesByProductType)
        .filter(quotes => Array.isArray(quotes))
        .flat();
    } else if (activeTab === 'new-annuity') {
      // Return all original annuity quotes as a flat array
      return Object.values(originalQuotesByProductType)
        .filter(quotes => Array.isArray(quotes))
        .flat();
    }
    return [];
  };

  // Check if filters are currently applied
  const hasActiveFilters = () => {
    const hasCarrierFilters = carrierFilter && Object.values(carrierFilter).some(filters => filters && filters.length > 0);
    return appliedFilters !== null || hasCarrierFilters;
  };

  // Helper function to map product type codes to full names
  const getFullProductTypeName = productTypeCode => {
    const productTypeMapping = {
      FA: 'Fixed Annuity',
      MYGA: 'Multi-Year Guaranteed Annuity',
      FIA: 'Fixed Indexed Annuity'
    };
    return productTypeMapping[productTypeCode] || productTypeCode || 'Fixed Annuity';
  };

  const handleDetailsClick = (quote, activeProductTab = null) => {
    // Find the carrier and product data for the quote
    let carrier = null;
    let product = null;

    if (activeTab === 'new-annuity') {
      // Find carrier from aaqCarriers
      carrier = aaqCarriers?.find(c => c.id === quote.carrier_id);
      // Find product from aaqProducts
      product = aaqProducts?.find(p => p.id === quote.product_id);
    } else if (activeTab === 'new-life') {
      // Find carrier from alqCarriers
      carrier = alqCarriers?.find(c => c.id === quote.carrier_id);
      // For life quotes, we might need to load product data or use quote data
      product = {
        product_name: quote.product_name,
        product_feature_ids: quote.product_feature_ids || []
        // Add other product fields as needed
      };
    }

    // Determine product type from context or quote data
    let productType = 'Fixed Annuity'; // default
    if (activeTab === 'new-annuity') {
      if (activeProductTab) {
        // Use the active product tab from QuoteResults (more reliable)
        productType = getFullProductTypeName(activeProductTab);
      } else {
        // Fallback to quote data
        productType = getFullProductTypeName(quote.product_type);
      }
    }

    // Set the modal data and open
    setDetailsModalData({
      quote,
      carrier,
      product,
      activeTab,
      formData,
      // Pass additional props based on quote type
      ...(activeTab === 'new-annuity' && {
        solveFor: formData.solveFor,
        productType
      }),
      ...(activeTab === 'new-life' && {
        solveFor: formData.solveFor || 'premium',
        premiumMode: 'annual', // Default or from formData
        productFeatures: alqProductFeatures?.data || []
      })
    });
    setIsDetailsModalOpen(true);
    // Reset hideQuoteCardDetails when opening a new details modal
    setHideQuoteCardDetails(false);
  };

  const handleStartEApp = quote => {
    // Open QuoteActionModal with the selected quote and current tab context
    setQuoteActionModalData({
      quote,
      quoteType: getFormType(),
      activeTab,
      formData,
      // Include additional context data needed by the modals
      agent: {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.agentEmail,
        phone: formData.agentPhone,
        insurance_license_number: formData.agentLicenseNumber
      },
      client: {
        first_name: formData.firstName, // These might need to be separate client fields
        last_name: formData.lastName,
        gender: formData.gender,
        date_of_birth: formData.dateOfBirth
      },
      ran_quote_data: {
        date_of_birth: formData.dateOfBirth || formData.dateOfBirthLife || '',
        gender: formData.gender || formData.genderLife || '',
        jurisdiction: formData.jurisdiction || formData.jurisdictionLife || '',
        face_amount: formData.faceAmounts?.[0] || 500000, // Use first face amount
        premium: formData.premiumLife,
        current_age: null, // Calculate from DOB if needed
        nearest_age: null // Calculate from DOB if needed
      }
    });
    setIsQuoteActionModalOpen(true);
  };

  const getFormType = () => {
    if (activeTab === 'new-life') return 'life';
    if (activeTab === 'new-ltc') return 'ltc';
    return 'annuity';
  };

  // Derive the underlying Life Quoter's token ID so modals can use it as source_guid
  const lifeQuoterTokenId = uqData?.universal_quoter_details?.find(detail => detail.quoter_type === 2)?.quoter_token_id || '';
  const annuityQuoterTokenId = uqData?.universal_quoter_details?.find(detail => detail?.quoter_type === 1)?.quoter_token_id || '';

  const styles = {
    container: {
      backgroundColor: '#f8f9fa',
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      position: 'relative'
    },
    disabledOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    },
    disabledMessage: {
      backgroundColor: '#fff',
      padding: '20px 40px',
      borderRadius: '8px',
      fontSize: '18px',
      fontWeight: '600',
      color: '#333',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      textAlign: 'center'
    },
    loadingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      pointerEvents: 'all'
    },
    quoteSetupOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    quoteSetupOverlayContent: {
      backgroundColor: '#fff',
      borderRadius: '12px',
      width: '90%',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
    },
    quoteSetupHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px 24px',
      borderBottom: '1px solid #e9ecef',
      backgroundColor: '#f8f9fa',
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px'
    },
    quoteSetupTitle: {
      margin: 0,
      fontSize: '20px',
      fontWeight: '600',
      color: '#333'
    },
    quoteSetupCloseButton: {
      background: 'none',
      border: '1px solid #017DB7',
      fontSize: '13px',
      cursor: 'pointer',
      color: '#017DB7',
      padding: '6px 16px',
      borderRadius: '4px',
      fontWeight: '400',
      transition: 'all 0.2s ease'
    },
    quoteSetupFormContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: 0
    }
  };
  // Delegate to the shared utility so the details-modal "Start eApp" button
  // enable/disable logic is always in sync with what QuoteActionModal renders.
  // Term includes both standard term and Return-of-Premium (ROP) products.
  const combinedTermProducts = [...(lifeProductOptions?.term_products || []), ...(lifeProductOptions?.return_of_premium_products || [])];
  const hasCustomActionsForQuote = quote => hasQuoteValidActions(quote, { quoteType: getFormType(), alqQuoter, aaqQuoter, termProducts: combinedTermProducts });

  const isStartEAppBtnEnabled = hasCustomActionsForQuote(detailsModalData?.quote);

  const renderGlobalConfirm = () => {
    if (!confirmationState) return null;

    const handleCancel = () => setConfirmationState(null);
    const handleContinue = () => {
      const action = confirmationState.onConfirm;
      setConfirmationState(null);
      if (typeof action === 'function') {
        action();
      }
    };

    return (
      <div
        aria-modal='true'
        role='dialog'
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: 8,
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            maxWidth: 420,
            width: '90%',
            padding: 20
          }}
        >
          <h3 style={{ margin: '0 0 8px', fontSize: 18, color: '#333' }}>{confirmationState.title || 'Please confirm'}</h3>
          <p style={{ margin: '0 0 16px', color: '#444' }}>{confirmationState.message}</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={handleCancel}
              style={{
                minWidth: 90,
                padding: '6px 18px',
                borderRadius: 4,
                border: '1px solid #d0d7de',
                backgroundColor: '#ffffff',
                color: '#57606a',
                fontSize: 14,
                fontWeight: 400,
                cursor: 'pointer'
              }}
              type='button'
            >
              Cancel
            </button>
            <button
              onClick={handleContinue}
              style={{
                minWidth: 100,
                padding: '6px 18px',
                borderRadius: 4,
                border: '1px solid #007bff',
                backgroundColor: '#007bff',
                color: '#ffffff',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer'
              }}
              type='button'
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* Small confirmation card used for HA / carrier-change confirmations */}
      {renderGlobalConfirm()}
      {/* API loading overlay - blocks interaction while requests are in flight */}
      {loading && (
        <div style={styles.loadingOverlay}>
          <Loader />
        </div>
      )}
      {/* Disabled overlay */}
      {disableQuoter && (
        <div style={styles.disabledOverlay}>
          <div style={styles.disabledMessage}>Universal Quoter is currently disabled</div>
        </div>
      )}
      <QuoterHeader
        activeTab={activeTab}
        compareSelections={compareSelections}
        currentView={currentView}
        formData={formData}
        hasActiveFilters={hasActiveFilters()}
        isUniversalQuoterLoaded={isUniversalQuoterLoaded}
        onCompareAction={handleCompareAction}
        onFiltersApply={handleFiltersApply}
        onFiltersClear={handleFiltersClear}
        onModalOpen={handleModal}
        onQuoteSetupToggle={handleQuoteSetupToggle}
        onShareAction={handleShareAction}
        onTabChange={handleTabChange}
        originalQuotesData={getOriginalQuotesData()}
        quotesData={getCurrentQuotesData()}
        universalQuoterDetails={uqData}
      />
      {currentView === 'form' && (
        <>
          {quotesError && (
            <div
              style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                padding: '12px',
                borderRadius: '4px',
                margin: '16px 20px',
                border: '1px solid #f5c6cb'
              }}
            >
              Error: {quotesError}
            </div>
          )}
          <OptimizedQuoteForm
            aaqCriteriaOptions={aaqCriteriaOptions}
            alqCarriers={alqCarriers}
            alqGuides={alqGuides}
            formData={formData}
            formType={getFormType()}
            hasHealthAnalyzer={alqQuoter?.is_health_analyzer_enabled || false}
            hasQuoteByPremium={alqQuoter?.enable_quote_by_premium || false}
            lifeProductOptions={lifeProductOptions}
            loading={loading}
            onInputChange={handleInputChange}
            onModalOpen={handleModal}
            onSubmit={handleSubmit}
            setUnderwritingClasses={setUnderwritingClasses}
            solveType={solveType}
            underwritingClasses={underwritingClasses}
            uqData={uqData}
          />
        </>
      )}
      {currentView === 'loading' && <LoadingState />}
      {currentView === 'results' && (
        <>
          {' '}
          <QuoteResults
            aaqQuoter={aaqQuoter}
            alqQuoter={alqQuoter}
            carrierFilter={carrierFilter}
            compareSelections={compareSelections}
            formData={formData}
            hasActiveFilters={hasActiveFilters()}
            hasQuotesBeenLoaded={hasQuotesBeenLoaded()}
            loadingStates={loadingStates}
            onClearFilters={handleFiltersClear}
            onCompareSelectionsChange={setCompareSelections}
            onDetailsClick={handleDetailsClick}
            onStartEApp={handleStartEApp}
            originalQuotesData={getOriginalQuotesData()}
            quotesByProductType={getFilteredQuotesByProductType()}
            quoteType={getFormType()}
            selectedProductTypes={
              activeTab === 'new-life'
                ? (() => {
                    // Combine selected product types with those that have quotes
                    const selectedTypes = formData.selectedProductTypes || [];
                    const typesWithQuotes = Object.keys(quotesByProductType).filter(pt => Array.isArray(quotesByProductType[pt]) && quotesByProductType[pt].length > 0);
                    // Merge both arrays and remove duplicates
                    const allTypes = [...new Set([...selectedTypes, ...typesWithQuotes])];
                    return allTypes;
                  })()
                : formData.productTypes || []
            }
          />
          {/* Quote Setup Overlay */}
          {isQuoteSetupOverlayOpen && (
            <div
              onClick={e => {
                // Close overlay when clicking on the backdrop
                if (e.target === e.currentTarget) {
                  handleQuoteSetupToggle();
                }
              }}
              style={styles.quoteSetupOverlay}
            >
              <div style={styles.quoteSetupOverlayContent}>
                <div style={styles.quoteSetupHeader}>
                  <h3 style={styles.quoteSetupTitle}>Quote Setup</h3>
                  <button onClick={handleQuoteSetupToggle} style={styles.quoteSetupCloseButton}>
                    Close
                  </button>
                </div>
                <div style={styles.quoteSetupFormContainer}>
                  {quotesError && (
                    <div
                      style={{
                        backgroundColor: '#f8d7da',
                        color: '#721c24',
                        padding: '12px',
                        borderRadius: '4px',
                        margin: '16px 20px',
                        border: '1px solid #f5c6cb'
                      }}
                    >
                      Error: {quotesError}
                    </div>
                  )}
                  <OptimizedQuoteForm
                    aaqCriteriaOptions={aaqCriteriaOptions}
                    alqCarriers={alqCarriers}
                    alqGuides={alqGuides}
                    formData={overlayFormData || formData}
                    formType={getFormType()}
                    hasHealthAnalyzer={alqQuoter?.is_health_analyzer_enabled || false}
                    hasQuoteByPremium={alqQuoter?.enable_quote_by_premium || false}
                    lifeProductOptions={lifeProductOptions}
                    loading={loading}
                    onInputChange={handleOverlayInputChange}
                    onModalOpen={handleModal}
                    onSubmit={handleSubmit}
                    setUnderwritingClasses={setUnderwritingClasses}
                    solveType={solveType}
                    underwritingClasses={underwritingClasses}
                    uqData={uqData}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <CarriersModal
        activeTab={activeTab}
        availableCarriers={isCarriersModalFilterMode ? getAvailableCarriersFromQuotes() : getAvailableCarriers()}
        isFilterMode={isCarriersModalFilterMode}
        isOpen={openModal === 'carriers'}
        onApplyFilter={handleCarrierFilterChange}
        onCarriersChange={isCarriersModalFilterMode ? handleCarrierFilterChange : handleCarriersChange}
        onClearFilter={handleClearCarrierFilter}
        onClose={handleModalClose}
        quotesByProductType={quotesByProductType}
        selectedCarriers={(() => {
          const carriers = isCarriersModalFilterMode ? carrierFilter : getCurrentFormCarriers();
          return carriers;
        })()}
        showProductTypeSections={isCarriersModalFilterMode}
      />
      {openModal === 'health-analyzer' && (alqQuoter?.is_health_analyzer_enabled || false) && (
        <HealthAnalyzerModal
          areCarriersEqual={areCarriersEqual(alqCarriers, healthAnalyzerCarriers)}
          availableCarriers={healthAnalyzerCarriers}
          dateOfBirth={formData.dateOfBirthLife || quoterConfig?.proposed_insured?.life?.date_of_birth || ''}
          faceAmount={formData.faceAmounts?.[0] || 500000}
          gender={formData.genderLife || quoterConfig?.proposed_insured?.life?.gender || 'Male'} // Use first face amount for Health Analyzer
          isolated={false}
          jurisdiction={formData.jurisdictionLife || quoterConfig?.proposed_insured?.life?.state || ''}
          onClose={handleModalClose}
          // Raw HA carrier selection (ids/objects) if the modal wants to show it.
          onHaCarriersEvaluated={handleHealthAnalyzerCarriersChange}
          // Keep old prop for backwards compatibility, but it now expects
          // the structured HA result instead of an array of ids.
          selectedCarriers={healthAnalyzerResults?.carrierValues || []}
          setSelectedCarriers={handleHealthAnalyzerCarriersChange}
          setUnderwritingClasses={setUnderwritingClasses}
          showReasoning={uqData?.is_health_analyzer_reasoning_enabled} // Allow Health Analyzer to sync with main form
        />
      )}
      {/* Comparison Modals */}
      {isCompareModalOpen &&
        compareModalData?.quotes?.length > 0 &&
        (activeTab === 'new-life' ? (
          <>
            <QuoteComparisonModal
              comparisonData={compareModalData}
              isOpen={isCompareModalOpen && compareModalData?.quotes?.length > 0}
              onClose={handleCompareModalClose}
              onShare={() => setOpenModal('share-quotes')}
            />
            {openModal === 'share-quotes' && (
              <ShareQuotesModal
                isCalifornia={overlayFormData ? overlayFormData?.jurisdiction === 'CA' : formData?.jurisdiction === 'CA'}
                onClose={handleModalClose}
                selectedQuotes={compareModalData.selectedQuotes}
              />
            )}
          </>
        ) : (
          <>
            <QuoteComparisonModal
              comparisonData={compareModalData}
              isOpen={isCompareModalOpen && compareModalData?.quotes?.length > 0}
              onClose={handleCompareModalClose}
              onShare={() => setOpenModal('share-quotes')}
            />
            {openModal === 'share-quotes' && (
              <ComparisonModal
                agent={{
                  email: compareModalData.formData.agentEmail,
                  first_name: compareModalData.formData.firstName,
                  last_name: compareModalData.formData.lastName,
                  phone: compareModalData.formData.agentPhone,
                  insurance_license_number: compareModalData.formData.agentLicenseNumber
                }}
                client={{
                  first_name: compareModalData.formData.firstName,
                  last_name: compareModalData.formData.lastName
                }}
                isCalifornia={overlayFormData ? overlayFormData?.jurisdiction === 'CA' : formData?.jurisdiction === 'CA'}
                onClose={handleModalClose}
                selectedQuotes={compareModalData.quotes?.map(quote => quote.id)}
                solve={
                  {
                    // Add solve data if needed
                  }
                }
              />
            )}
          </>
        ))}
      {/* Details Modal - Conditional QuoteCard based on active tab */}
      {isDetailsModalOpen && detailsModalData && (
        <Modal
          maxWidth={1000}
          onClose={handleDetailsModalClose}
          title={`${detailsModalData.carrier?.name || 'Carrier'} - ${detailsModalData.product?.product_name || detailsModalData.quote?.product_name || 'Product'}`}
        >
          {activeTab === 'new-annuity' ? (
            <ThemeContext.Provider value={{ color: '#007bff', accent_color: '#28a745' }}>
              <AnnuityQuoteCard
                carrier={detailsModalData.carrier}
                hasActions={true}
                index={0}
                isSelected={false}
                isStartEAppBtnEnabled={isStartEAppBtnEnabled}
                isUniversalQuoter={true} // No actions in modal view
                onSelect={() => {}} // Change button text to "Share" for annuity products
                onShowActions={() => setShowPdfModal(true)} // Can open directly show PDF actions with the quote guid
                onStartEApp={handleStartEApp} // No-op since no selection in modal
                product={detailsModalData.product} // Empty since modal view
                productType={detailsModalData.productType} // No-op since no actions
                quote={detailsModalData.quote}
                selectedQuotes={[]} // Add Start eApp functionality
                showActionsBtnText='Share'
                showDetails={!hideQuoteCardDetails} // Hide details if QuoteActionModal is open
                solveFor={detailsModalData.solveFor}
              />
            </ThemeContext.Provider>
          ) : activeTab === 'new-life' ? (
            <ProductContext.Provider
              value={{
                color: '#007bff',
                agent:
                  QUOTER_CONFIG?.embed_context === 'fire_light'
                    ? firelightIXNModalData?.agent
                    : {
                        first_name: detailsModalData.formData.firstName || '',
                        last_name: detailsModalData.formData.lastName || '',
                        email: detailsModalData.formData.agentEmail || '',
                        phone: detailsModalData.formData.agentPhone || '',
                        insurance_license_number: detailsModalData.formData.agentLicenseNumber || ''
                      },
                client:
                  QUOTER_CONFIG?.embed_context === 'fire_light'
                    ? firelightIXNModalData?.client
                    : {
                        first_name: '',
                        last_name: ''
                      },
                ran_quote_data: {
                  date_of_birth: detailsModalData.formData.dateOfBirth || detailsModalData.formData.dateOfBirthLife || '',
                  gender: detailsModalData.formData.gender || detailsModalData.formData.genderLife || '',
                  face_amount: detailsModalData.formData.faceAmount,
                  product_types: detailsModalData.formData.selectedProductTypes || []
                },
                alq: {
                  agency_id: alqQuoter?.agency_id || '',
                  quoter_token_id: lifeQuoterTokenId,
                  agency_name: '',
                  custom_actions: alqQuoter?.custom_actions || [],
                  embed_config_quote_actions: (typeof window !== 'undefined' && window.UQ_EMBED_CONFIG_QUOTE_ACTIONS) || alqQuoter?.embed_config_quote_actions || [],
                  allow_forms: false,
                  term_products: lifeProductOptions?.term_products || [],
                  return_of_premium_products: lifeProductOptions?.return_of_premium_products || []
                },
                quoter_config: {
                  quote_params: [],
                  embed_context: QUOTER_CONFIG?.embed_context
                }
              }}
            >
              <LifeQuoteCard
                customAction={null}
                hasActions={false}
                isStartEAppBtnEnabled={isStartEAppBtnEnabled}
                isUniversalQuoter={true}
                onOpenFirelightModal={handleOpenFirelightModal}
                onSelect={() => {}} // Change button text to "Share" for life products
                onShowActions={() => setShowPdfModal(true)} // Can open directly show PDF actions with the quote guid
                onStartEApp={handleStartEApp}
                premiumMode={detailsModalData.premiumMode} // No-op since no selection in modal
                productFeatures={detailsModalData.productFeatures}
                quote={detailsModalData.quote}
                selected={false} // No actions in modal view
                showActionsBtnText='Share' // Add Start eApp functionality
                showDetails={isQuoteActionModalOpen ? false : !hideQuoteCardDetails}
                solveFor={detailsModalData.solveFor}
              />
            </ProductContext.Provider>
          ) : null}
        </Modal>
      )}{' '}
      {/* FirelightSimplifiedEticketModal */}
      {isFirelightModalOpen && firelightModalData && (
        <FirelightSimplifiedEticketModal
          agent={QUOTER_CONFIG?.embed_context === 'fire_light' ? firelightIXNModalData?.agent : firelightModalData.agent}
          appGuid={firelightModalData.appGuid}
          client={QUOTER_CONFIG?.embed_context === 'fire_light' ? firelightIXNModalData?.client : firelightModalData.client}
          onClose={handleFirelightModalClose}
          quote={firelightModalData.quote}
          ran_quote_data={firelightModalData.ran_quote_data}
        />
      )}
      {/* QuoteActionModal - conditionally render based on quote type */}
      {isQuoteActionModalOpen && quoteActionModalData && (
        <>
          {quoteActionModalData.quoteType === 'annuity' ? (
            <AnnuityProductContext.Provider
              value={{
                color: '#007bff',
                agent: QUOTER_CONFIG?.embed_context === 'fire_light' ? firelightIXNModalData?.agent : quoteActionModalData?.agent,
                client: QUOTER_CONFIG?.embed_context === 'fire_light' ? firelightIXNModalData?.client : quoteActionModalData.client,
                ran_quote_data: quoteActionModalData?.ran_quote_data,
                aaq: {
                  agency_id: aaqQuoter?.agency_id || '',
                  quoter_token_id: annuityQuoterTokenId,
                  agency_name: '',
                  custom_actions: aaqQuoter?.custom_actions || [],
                  embed_config_quote_actions: (typeof window !== 'undefined' && window?.UQ_EMBED_CONFIG_QUOTE_ACTIONS) || aaqQuoter?.embed_config_quote_actions || [],
                  allow_forms: false
                },
                quoter_config: {
                  quote_params: [],
                  embed_context: QUOTER_CONFIG?.embed_context
                }
              }}
            >
              <AnnuityQuoteActionModal
                actions={aaqQuoter?.quote_actions || []}
                onClose={handleQuoteActionModalClose}
                quote={quoteActionModalData.quote}
                quoteData={{
                  client: quoteActionModalData.client,
                  agent: quoteActionModalData.agent,
                  ...quoteActionModalData.ran_quote_data
                }}
              />
            </AnnuityProductContext.Provider>
          ) : quoteActionModalData.quoteType === 'life' ? (
            <ProductContext.Provider
              value={{
                color: '#007bff',
                agent: QUOTER_CONFIG?.embed_context === 'fire_light' ? firelightIXNModalData?.agent : quoteActionModalData.agent,
                client: QUOTER_CONFIG?.embed_context === 'fire_light' ? firelightIXNModalData?.client : quoteActionModalData.client,
                ran_quote_data: quoteActionModalData.ran_quote_data,
                alq: {
                  agency_id: alqQuoter?.agency_id || '',
                  quoter_token_id: lifeQuoterTokenId,
                  agency_name: '',
                  custom_actions: alqQuoter?.custom_actions || [],
                  embed_config_quote_actions: (typeof window !== 'undefined' && window.UQ_EMBED_CONFIG_QUOTE_ACTIONS) || alqQuoter?.embed_config_quote_actions || [],
                  allow_forms: false,
                  term_products: lifeProductOptions?.term_products || [],
                  return_of_premium_products: lifeProductOptions?.return_of_premium_products || []
                },
                quoter_config: {
                  quote_params: [],
                  embed_context: QUOTER_CONFIG?.embed_context
                }
              }}
            >
              <LifeQuoteActionModal onAction={handleLifeQuoteAction} onClose={handleQuoteActionModalClose} onOpenFirelightModal={handleOpenFirelightModal} quote={quoteActionModalData.quote} />
            </ProductContext.Provider>
          ) : null}
        </>
      )}
      {/* PDF Action Modal - rendered text, email and download for PDF actions */}
      {showPdfModal &&
        (activeTab === 'new-life' ? (
          <ShareQuotesModal
            isCalifornia={overlayFormData ? overlayFormData?.jurisdiction === 'CA' : formData?.jurisdiction === 'CA'}
            onClose={() => setShowPdfModal(false)}
            selectedQuotes={[detailsModalData?.quote?.guid]}
          />
        ) : (
          <PdfActions action={null} isCalifornia={formData?.jurisdiction === 'CA'} onClose={() => setShowPdfModal(false)} selectedQuotes={[detailsModalData?.quote?.id]} />
        ))}
      {/* Share Quotes Modal - rendered text, email and download for PDF actions */}
      {isShareModelOpen &&
        shareModelData &&
        (activeTab === 'new-life' ? (
          <>
            <ShareQuotesModal
              isCalifornia={overlayFormData ? overlayFormData?.jurisdiction === 'CA' : formData?.jurisdiction === 'CA'}
              onClose={handleShareModalClose}
              selectedQuotes={shareModelData.selectedQuotes}
            />
          </>
        ) : (
          <ComparisonModal
            agent={{
              email: shareModelData.formData.agentEmail,
              first_name: shareModelData.formData.firstName,
              last_name: shareModelData.formData.lastName,
              phone: shareModelData.formData.agentPhone,
              insurance_license_number: shareModelData.formData.agentLicenseNumber
            }}
            client={{
              first_name: shareModelData.formData.firstName,
              last_name: shareModelData.formData.lastName
            }}
            isCalifornia={overlayFormData ? overlayFormData?.jurisdiction === 'CA' : formData?.jurisdiction === 'CA'}
            onClose={handleShareModalClose}
            selectedQuotes={shareModelData.quotes}
            solve={
              {
                // Add solve data if needed
              }
            }
          />
        ))}
      {/* Follow-up modals for custom actions from QuoteActionModal */}
      {followUpModalType === 'open_url' && followUpModalData && (
        <OpenUrlModal
          inline={false}
          onClose={() => {
            setFollowUpModalType(null);
            setFollowUpModalData(null);
          }}
          url={followUpModalData.url || followUpModalData.action_data?.url || ''}
        />
      )}
      {followUpModalType === 'webhook_post' && followUpModalData && (
        <PostToUrlModal
          action={followUpModalData}
          onClose={() => {
            setFollowUpModalType(null);
            setFollowUpModalData(null);
          }}
          params={{
            quote: quoteActionModalData?.quote,
            agent: quoteActionModalData?.agent,
            client: quoteActionModalData?.client,
            ...quoteActionModalData?.ran_quote_data
          }}
        />
      )}
      {/* PSG eApp Modal */}
      {followUpModalType === 'psg_eapp' && quoteActionModalData?.quote && (
        <ProductContext.Provider
          value={{
            color: '#007bff',
            agent:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.agent
                : quoteActionModalData?.agent || {
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    email: formData.agentEmail,
                    phone: formData.agentPhone,
                    insurance_license_number: formData.agentLicenseNumber,
                    psg_agent_signature_code: ''
                  },
            client:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.client
                : quoteActionModalData?.client || {
                    first_name: '',
                    last_name: ''
                  },
            ran_quote_data: quoteActionModalData?.ran_quote_data || {},
            alq: {
              agency_name: '',
              custom_actions: alqQuoter?.custom_actions || [],
              embed_config_quote_actions: (typeof window !== 'undefined' && window.UQ_EMBED_CONFIG_QUOTE_ACTIONS) || alqQuoter?.embed_config_quote_actions || [],
              allow_forms: false,
              use_psg_test_servers: alqQuoter?.use_psg_test_servers || false
            },
            quoter_config: {
              quote_params: [],
              embed_context: QUOTER_CONFIG?.embed_context
            }
          }}
        >
          <PsgEappModal
            eapp={psgEapp}
            onClose={() => {
              setFollowUpModalType(null);
              setFollowUpModalData(null);
            }}
            onCreate={setPsgEapp}
            quote={quoteActionModalData.quote}
          />
        </ProductContext.Provider>
      )}
      {/* Anico E-Ticket Modal (LifeDX) */}
      {followUpModalType === 'anico' && quoteActionModalData?.quote && (
        <ProductContext.Provider
          value={{
            color: '#007bff',
            agent:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.agent
                : quoteActionModalData?.agent || {
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    email: formData.agentEmail,
                    phone: formData.agentPhone,
                    insurance_license_number: formData.agentLicenseNumber
                  },
            client:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.client
                : quoteActionModalData?.client || {
                    first_name: '',
                    last_name: ''
                  },
            ran_quote_data: quoteActionModalData?.ran_quote_data || {},
            alq: {
              agency_id: alqQuoter?.agency_id || '',
              quoter_token_id: lifeQuoterTokenId,
              agency_name: '',
              custom_actions: alqQuoter?.custom_actions || [],
              embed_config_quote_actions: (typeof window !== 'undefined' && window.UQ_EMBED_CONFIG_QUOTE_ACTIONS) || alqQuoter?.embed_config_quote_actions || [],
              allow_forms: false,
              use_anico_test_servers: alqQuoter?.use_anico_test_servers || false
            },
            quoter_config: {
              quote_params: [],
              embed_context: QUOTER_CONFIG?.embed_context
            }
          }}
        >
          <LifedxEticketModal
            eticketGuid={anicoEticketGuid}
            formData={[
              { name: 'agency_name', value: '' },
              { name: 'pi_first_name', value: quoteActionModalData?.client?.first_name || '' },
              { name: 'pi_last_name', value: quoteActionModalData?.client?.last_name || '' },
              { name: 'pi_date_of_birth', value: quoteActionModalData?.ran_quote_data?.date_of_birth || '' },
              { name: 'producer_first_name', value: quoteActionModalData?.agent?.first_name || '' },
              { name: 'producer_last_name', value: quoteActionModalData?.agent?.last_name || '' },
              { name: 'producer_email', value: quoteActionModalData?.agent?.email || '' },
              { name: 'producer_phone', value: quoteActionModalData?.agent?.phone || '' },
              { name: 'is_test', value: alqQuoter?.use_anico_test_servers || false }
            ]}
            key={anicoEticketGuid}
            onClose={() => {
              setFollowUpModalType(null);
              setFollowUpModalData(null);
            }}
            onCreate={setAnicoEticketGuid}
            provider='anico'
            quote={quoteActionModalData.quote}
            title={followUpModalData?.label || 'Anico E-Ticket'}
            token={alqQuoter?.use_anico_test_servers ? ANICO_TEST_EMBED_TOKEN : ANICO_EMBED_TOKEN}
          />
        </ProductContext.Provider>
      )}
      {/* Protective E-Ticket Modal (LifeDX) */}
      {followUpModalType === 'protective' && quoteActionModalData?.quote && (
        <ProductContext.Provider
          value={{
            color: '#007bff',
            agent:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.agent
                : quoteActionModalData?.agent || {
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    email: formData.agentEmail,
                    phone: formData.agentPhone,
                    insurance_license_number: formData.agentLicenseNumber
                  },
            client:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.client
                : quoteActionModalData?.client || {
                    first_name: '',
                    last_name: ''
                  },
            ran_quote_data: quoteActionModalData?.ran_quote_data || {},
            alq: {
              agency_id: alqQuoter?.agency_id || '',
              quoter_token_id: lifeQuoterTokenId,
              agency_name: '',
              custom_actions: alqQuoter?.custom_actions || [],
              embed_config_quote_actions: (typeof window !== 'undefined' && window.UQ_EMBED_CONFIG_QUOTE_ACTIONS) || alqQuoter?.embed_config_quote_actions || [],
              allow_forms: false,
              use_protective_test_servers: alqQuoter?.use_protective_test_servers || false,
              protective_agency_code: alqQuoter?.protective_agency_code || '',
              protective_contract_number: alqQuoter?.protective_contract_number || ''
            },
            quoter_config: {
              quote_params: [],
              embed_context: QUOTER_CONFIG?.embed_context
            }
          }}
        >
          <LifedxEticketModal
            eticketGuid={protectiveEticketGuid}
            formData={[
              { name: 'agency_name', value: '' },
              { name: 'pi_first_name', value: quoteActionModalData?.client?.first_name || '' },
              { name: 'pi_last_name', value: quoteActionModalData?.client?.last_name || '' },
              { name: 'pi_date_of_birth', value: quoteActionModalData?.ran_quote_data?.date_of_birth || '' },
              { name: 'producer_first_name', value: quoteActionModalData?.agent?.first_name || '' },
              { name: 'producer_last_name', value: quoteActionModalData?.agent?.last_name || '' },
              { name: 'producer_email', value: quoteActionModalData?.agent?.email || '' },
              { name: 'producer_phone', value: quoteActionModalData?.agent?.phone || '' },
              { name: 'first_name', value: quoteActionModalData?.client?.first_name || '' },
              { name: 'last_name', value: quoteActionModalData?.client?.last_name || '' },
              { name: 'agent_first_name', value: quoteActionModalData?.agent?.first_name || '' },
              { name: 'agent_last_name', value: quoteActionModalData?.agent?.last_name || '' },
              { name: 'agent_phone', value: quoteActionModalData?.agent?.phone || '' },
              { name: 'agent_email', value: quoteActionModalData?.agent?.email || '' },
              { name: 'is_test', value: alqQuoter?.use_protective_test_servers || false },
              { name: 'agency_code', value: alqQuoter?.protective_agency_code || '' },
              { name: 'agency_contract_number', value: alqQuoter?.protective_contract_number || '' }
            ]}
            key={protectiveEticketGuid}
            onClose={() => {
              setFollowUpModalType(null);
              setFollowUpModalData(null);
            }}
            onCreate={setProtectiveEticketGuid}
            provider='protective_life'
            quote={quoteActionModalData.quote}
            title={followUpModalData?.label || 'Protective E-Ticket'}
            token={alqQuoter?.use_protective_test_servers ? PROTECTIVE_TEST_EMBED_TOKEN : PROTECTIVE_EMBED_TOKEN}
          />
        </ProductContext.Provider>
      )}
      {/* PacLife E-Ticket Modal (LifeDX) */}
      {followUpModalType === 'paclife' && quoteActionModalData?.quote && (
        <ProductContext.Provider
          value={{
            color: '#007bff',
            agent:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.agent
                : quoteActionModalData?.agent || {
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    email: formData.agentEmail,
                    phone: formData.agentPhone,
                    insurance_license_number: formData.agentLicenseNumber
                  },
            client:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.client
                : quoteActionModalData?.client || {
                    first_name: '',
                    last_name: ''
                  },
            ran_quote_data: quoteActionModalData?.ran_quote_data || {},
            alq: {
              agency_id: alqQuoter?.agency_id || '',
              quoter_token_id: lifeQuoterTokenId,
              agency_name: '',
              custom_actions: alqQuoter?.custom_actions || [],
              embed_config_quote_actions: (typeof window !== 'undefined' && window.UQ_EMBED_CONFIG_QUOTE_ACTIONS) || alqQuoter?.embed_config_quote_actions || [],
              allow_forms: false,
              use_paclife_test_servers: alqQuoter?.use_paclife_test_servers || false
            },
            quoter_config: {
              quote_params: [],
              embed_context: QUOTER_CONFIG?.embed_context
            }
          }}
        >
          <LifedxEticketModal
            eticketGuid={paclifeEticketGuid}
            formData={[
              { name: 'agency_name', value: '' },
              { name: 'pi_first_name', value: quoteActionModalData?.client?.first_name || '' },
              { name: 'pi_last_name', value: quoteActionModalData?.client?.last_name || '' },
              { name: 'pi_date_of_birth', value: quoteActionModalData?.ran_quote_data?.date_of_birth || '' },
              { name: 'producer_first_name', value: quoteActionModalData?.agent?.first_name || '' },
              { name: 'producer_last_name', value: quoteActionModalData?.agent?.last_name || '' },
              { name: 'producer_email', value: quoteActionModalData?.agent?.email || '' },
              { name: 'producer_phone', value: quoteActionModalData?.agent?.phone || '' },
              { name: 'is_test', value: alqQuoter?.use_paclife_test_servers || false }
            ]}
            key={paclifeEticketGuid}
            onClose={() => {
              setFollowUpModalType(null);
              setFollowUpModalData(null);
            }}
            onCreate={setPaclifeEticketGuid}
            provider='paclife'
            quote={quoteActionModalData.quote}
            title={followUpModalData?.label || 'PacLife E-Ticket'}
            token={alqQuoter?.use_paclife_test_servers ? PACLIFE_TEST_EMBED_TOKEN : PACLIFE_EMBED_TOKEN}
          />
        </ProductContext.Provider>
      )}
      {/* SBLI E-Ticket Modal (LifeDX) */}
      {followUpModalType === 'sbli' && quoteActionModalData?.quote && (
        <ProductContext.Provider
          value={{
            color: '#007bff',
            agent:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.agent
                : quoteActionModalData?.agent || {
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    email: formData.agentEmail,
                    phone: formData.agentPhone,
                    insurance_license_number: formData.agentLicenseNumber
                  },
            client:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.client
                : quoteActionModalData?.client || {
                    first_name: '',
                    last_name: ''
                  },
            ran_quote_data: quoteActionModalData?.ran_quote_data || {},
            alq: {
              agency_id: alqQuoter?.agency_id || '',
              quoter_token_id: lifeQuoterTokenId,
              agency_name: '',
              custom_actions: alqQuoter?.custom_actions || [],
              embed_config_quote_actions: (typeof window !== 'undefined' && window.UQ_EMBED_CONFIG_QUOTE_ACTIONS) || alqQuoter?.embed_config_quote_actions || [],
              allow_forms: false,
              use_sbli_test_servers: alqQuoter?.use_sbli_test_servers || false,
              sbli_agency_code: alqQuoter?.sbli_agency_code || ''
            },
            quoter_config: {
              quote_params: [],
              embed_context: QUOTER_CONFIG?.embed_context
            }
          }}
        >
          <LifedxEticketModal
            eticketGuid={sbliEticketGuid}
            formData={[
              { name: 'agency_name', value: '' },
              { name: 'pi_first_name', value: quoteActionModalData?.client?.first_name || '' },
              { name: 'pi_last_name', value: quoteActionModalData?.client?.last_name || '' },
              { name: 'pi_date_of_birth', value: quoteActionModalData?.ran_quote_data?.date_of_birth || '' },
              { name: 'producer_first_name', value: quoteActionModalData?.agent?.first_name || '' },
              { name: 'producer_last_name', value: quoteActionModalData?.agent?.last_name || '' },
              { name: 'producer_email', value: quoteActionModalData?.agent?.email || '' },
              { name: 'producer_phone', value: quoteActionModalData?.agent?.phone || '' },
              { name: 'is_test', value: alqQuoter?.use_sbli_test_servers || false },
              { name: 'agency_code', value: alqQuoter?.sbli_agency_code || '' }
            ]}
            key={sbliEticketGuid}
            onClose={() => {
              setFollowUpModalType(null);
              setFollowUpModalData(null);
            }}
            onCreate={setSbliEticketGuid}
            provider='sbli'
            quote={quoteActionModalData.quote}
            title={followUpModalData?.label || 'SBLI E-Ticket'}
            token={alqQuoter?.use_sbli_test_servers ? SBLI_TEST_EMBED_TOKEN : SBLI_EMBED_TOKEN}
          />
        </ProductContext.Provider>
      )}
      {/* NMB Term E-Ticket Modal */}
      {followUpModalType === 'nmb_term_eticket' && quoteActionModalData?.quote && (
        <ProductContext.Provider
          value={{
            color: '#007bff',
            agent:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.agent
                : quoteActionModalData?.agent || {
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    email: formData.agentEmail,
                    phone: formData.agentPhone,
                    insurance_license_number: formData.agentLicenseNumber
                  },
            client:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.client
                : quoteActionModalData?.client || {
                    first_name: '',
                    last_name: ''
                  },
            ran_quote_data: quoteActionModalData?.ran_quote_data || {},
            alq: {
              agency_id: alqQuoter?.agency_id || '',
              quoter_token_id: lifeQuoterTokenId,
              agency_name: '',
              custom_actions: alqQuoter?.custom_actions || [],
              embed_config_quote_actions: (typeof window !== 'undefined' && window.UQ_EMBED_CONFIG_QUOTE_ACTIONS) || alqQuoter?.embed_config_quote_actions || [],
              allow_forms: false
            },
            quoter_config: {
              quote_params: [],
              embed_context: QUOTER_CONFIG?.embed_context
            }
          }}
        >
          <NmbTermEticketModal
            eticketGuid={nmbEticketGuid}
            onClose={() => {
              setFollowUpModalType(null);
              setFollowUpModalData(null);
            }}
            onCreate={setNmbEticketGuid}
            quote={quoteActionModalData.quote}
          />
        </ProductContext.Provider>
      )}
      {/* NMB Whole Life E-Ticket Modal */}
      {followUpModalType === 'nmb_whole_life_eticket' && quoteActionModalData?.quote && (
        <ProductContext.Provider
          value={{
            color: '#007bff',
            agent:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.agent
                : quoteActionModalData?.agent || {
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    email: formData.agentEmail,
                    phone: formData.agentPhone,
                    insurance_license_number: formData.agentLicenseNumber
                  },
            client:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.client
                : quoteActionModalData?.client || {
                    first_name: '',
                    last_name: ''
                  },
            ran_quote_data: quoteActionModalData?.ran_quote_data || {},
            alq: {
              agency_id: alqQuoter?.agency_id || '',
              quoter_token_id: lifeQuoterTokenId,
              agency_name: '',
              custom_actions: alqQuoter?.custom_actions || [],
              embed_config_quote_actions: (typeof window !== 'undefined' && window.UQ_EMBED_CONFIG_QUOTE_ACTIONS) || alqQuoter?.embed_config_quote_actions || [],
              allow_forms: false
            },
            quoter_config: {
              quote_params: [],
              embed_context: QUOTER_CONFIG?.embed_context
            }
          }}
        >
          <NmbWholeLifeEticketModal
            eticketGuid={nmbEticketGuid}
            onClose={() => {
              setFollowUpModalType(null);
              setFollowUpModalData(null);
            }}
            onCreate={setNmbEticketGuid}
            quote={quoteActionModalData.quote}
          />
        </ProductContext.Provider>
      )}
      {/* Firelight E-Ticket Modal */}
      {followUpModalType === 'firelight' && followUpModalData?.action_data?.activity === 'e_ticket' && quoteActionModalData?.quote && (
        <ProductContext.Provider
          value={{
            color: '#007bff',
            agent:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.agent
                : quoteActionModalData?.agent || {
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    email: formData.agentEmail,
                    phone: formData.agentPhone,
                    insurance_license_number: formData.agentLicenseNumber
                  },
            client:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.client
                : quoteActionModalData?.client || {
                    first_name: '',
                    last_name: ''
                  },
            ran_quote_data: quoteActionModalData?.ran_quote_data || {},
            alq: {
              agency_id: alqQuoter?.agency_id || '',
              quoter_token_id: lifeQuoterTokenId,
              agency_name: '',
              custom_actions: alqQuoter?.custom_actions || [],
              embed_config_quote_actions: (typeof window !== 'undefined' && window.UQ_EMBED_CONFIG_QUOTE_ACTIONS) || alqQuoter?.embed_config_quote_actions || [],
              allow_forms: false
            },
            quoter_config: {
              quote_params: [],
              embed_context: QUOTER_CONFIG?.embed_context
            }
          }}
        >
          <FirelightEticketModal
            action={followUpModalData}
            eticket={firelightEticket}
            onClose={() => {
              setFollowUpModalType(null);
              setFollowUpModalData(null);
            }}
            onSave={setFirelightEticket}
            quote={quoteActionModalData.quote}
          />
        </ProductContext.Provider>
      )}
      {/* Firelight E-App Modal */}
      {followUpModalType === 'firelight' && followUpModalData?.action_data?.activity === 'e_app' && quoteActionModalData?.quote && (
        <ProductContext.Provider
          value={{
            color: '#007bff',
            agent:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.agent
                : quoteActionModalData?.agent || {
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    email: formData.agentEmail,
                    phone: formData.agentPhone,
                    insurance_license_number: formData.agentLicenseNumber
                  },
            client:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.client
                : quoteActionModalData?.client || {
                    first_name: '',
                    last_name: ''
                  },
            ran_quote_data: quoteActionModalData?.ran_quote_data || {},
            alq: {
              agency_id: alqQuoter?.agency_id || '',
              quoter_token_id: lifeQuoterTokenId,
              agency_name: '',
              custom_actions: alqQuoter?.custom_actions || [],
              embed_config_quote_actions: (typeof window !== 'undefined' && window.UQ_EMBED_CONFIG_QUOTE_ACTIONS) || alqQuoter?.embed_config_quote_actions || [],
              allow_forms: false
            },
            quoter_config: {
              quote_params: [],
              embed_context: QUOTER_CONFIG?.embed_context
            }
          }}
        >
          <FirelightEappModal
            action={followUpModalData}
            eapp={firelightEapp}
            onClose={() => {
              setFollowUpModalType(null);
              setFollowUpModalData(null);
            }}
            onSave={setFirelightEapp}
            quote={quoteActionModalData.quote}
          />
        </ProductContext.Provider>
      )}
      {/* Request Forms Modal */}
      {followUpModalType === 'request_forms' && quoteActionModalData?.quote && (
        <ProductContext.Provider
          value={{
            color: '#007bff',
            agent:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.agent
                : quoteActionModalData?.agent || {
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    email: formData.agentEmail,
                    phone: formData.agentPhone,
                    insurance_license_number: formData.agentLicenseNumber
                  },
            client:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.client
                : quoteActionModalData?.client || {
                    first_name: '',
                    last_name: ''
                  },
            ran_quote_data: quoteActionModalData?.ran_quote_data || {},
            alq: {
              agency_id: alqQuoter?.agency_id || '',
              quoter_token_id: lifeQuoterTokenId,
              agency_name: '',
              custom_actions: alqQuoter?.custom_actions || [],
              embed_config_quote_actions: (typeof window !== 'undefined' && window.UQ_EMBED_CONFIG_QUOTE_ACTIONS) || alqQuoter?.embed_config_quote_actions || [],
              allow_forms: false
            },
            quoter_config: {
              quote_params: [],
              embed_context: QUOTER_CONFIG?.embed_context
            }
          }}
        >
          <RequestFormsModal
            onBackClick={() => {
              setFollowUpModalType(null);
              setFollowUpModalData(null);
            }}
            onClose={() => {
              setFollowUpModalType(null);
              setFollowUpModalData(null);
            }}
            quote={quoteActionModalData.quote}
          />
        </ProductContext.Provider>
      )}
      {/* Applicint Modal */}
      {followUpModalType === 'applicint' && quoteActionModalData?.quote && (
        <ProductContext.Provider
          value={{
            color: '#007bff',
            agent:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.agent
                : quoteActionModalData?.agent || {
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    email: formData.agentEmail,
                    phone: formData.agentPhone,
                    insurance_license_number: formData.agentLicenseNumber
                  },
            client:
              QUOTER_CONFIG?.embed_context === 'fire_light'
                ? firelightIXNModalData?.client
                : quoteActionModalData?.client || {
                    first_name: '',
                    last_name: ''
                  },
            ran_quote_data: quoteActionModalData?.ran_quote_data || {},
            alq: {
              agency_id: alqQuoter?.agency_id || '',
              quoter_token_id: lifeQuoterTokenId,
              agency_name: '',
              custom_actions: alqQuoter?.custom_actions || [],
              embed_config_quote_actions: (typeof window !== 'undefined' && window.UQ_EMBED_CONFIG_QUOTE_ACTIONS) || alqQuoter?.embed_config_quote_actions || [],
              allow_forms: false
            },
            quoter_config: {
              quote_params: [],
              embed_context: QUOTER_CONFIG?.embed_context
            }
          }}
        >
          <ApplicintModal
            onBackClick={() => {
              setFollowUpModalType(null);
              setFollowUpModalData(null);
            }}
            onClose={() => {
              setFollowUpModalType(null);
              setFollowUpModalData(null);
            }}
            quote={quoteActionModalData.quote}
          />
        </ProductContext.Provider>
      )}
    </div>
  );
};

export default UniversalQuoterComponent;
